import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Button, Input } from 'reactstrap';

import config from '../../config';
import * as userUrls from '../../urls/api-users/admin';

const apiFilesHost = config.config.api_files.host;

const buildDriverAdminUrl = (driverIdentifier, suffix) =>
  `${userUrls.dispatchUsersHost || userUrls.usersHost}/admin/driver/${encodeURIComponent(driverIdentifier)}${suffix}`;

const requestDriverAdmin = async ({ driverIdentifier, suffix, method = 'get', data = undefined, params = undefined, headers = null }) => {
  const primaryUrl = buildDriverAdminUrl(driverIdentifier, suffix);
  const fallbackUrl = userUrls.toHostedUsersUrl(primaryUrl);
  const requestConfig = {
    method,
    url: primaryUrl,
    timeout: DRIVER_COMPLIANCE_REQUEST_TIMEOUT_MS,
    headers: headers || getAuthHeaders(),
    ...(typeof data !== 'undefined' ? { data } : {}),
    ...(typeof params !== 'undefined' ? { params } : {}),
  };

  const shouldRetryHosted = error => {
    const status = error && error.response ? error.response.status : null;

    if (!fallbackUrl || fallbackUrl === primaryUrl) {
      return false;
    }

    if (status === 401 || status === 403 || status === 404) {
      return false;
    }

    if (status === null) {
      return true;
    }

    return status >= 500;
  };

  try {
    return await axios(requestConfig);
  } catch (error) {
    if (!shouldRetryHosted(error)) {
      throw error;
    }

    return axios({
      ...requestConfig,
      url: fallbackUrl,
    });
  }
};

const VEHICLE_OPTIONS = [
  { value: 'compact_car', label: 'Compact Car' },
  { value: 'sedan', label: 'Sedan' },
  { value: 'suv', label: 'SUV' },
  { value: 'minivan', label: 'Minivan' },
  { value: 'pickup_truck', label: '1/2 Ton Pickup' },
  { value: 'truck', label: '1/2 Ton Pickup' },
  { value: 'sprinter_van', label: 'Sprinter Van' },
  { value: 'box_truck', label: 'Box Truck' },
  { value: 'tractor_semi', label: 'Semi/Tractor' }
];

const ASSET_OPTIONS = [
  { value: 'bumper_pull', label: 'Bumper Pull' },
  { value: 'gooseneck', label: 'Gooseneck' },
  { value: 'dry_van', label: 'Dry Van' },
  { value: 'flatbed', label: 'Flatbed' },
  { value: 'reefer', label: 'Reefer' },
  { value: 'lift_gate', label: 'Lift Gate' }
];

const REQUIRED_DOCUMENT_DEFINITIONS = [
  { type: 'drivers_license', label: "Driver's License" },
  { type: 'proof_of_insurance', label: 'Insurance' },
  { type: 'vehicle_registration', label: 'Registration' }
];

const REQUIRED_DOCUMENT_ORDER = {
  drivers_license: 0,
  proof_of_insurance: 1,
  vehicle_registration: 2,
};

const CREDENTIAL_DOCUMENT_DEFINITIONS = [
  { type: 'twic', label: 'TWIC' },
  { type: 'hazmat', label: 'Hazmat' },
  { type: 'cdl', label: 'CDL' },
  { type: 'lift_gate', label: 'Lift Gate' }
];

const CREDENTIAL_UPLOAD_OPTIONS = CREDENTIAL_DOCUMENT_DEFINITIONS.filter(option => option.type !== 'lift_gate');

const ONBOARDING_TIER_OPTIONS = [
  { value: 'non_dot', label: 'Non-DOT' },
  { value: 'dot', label: 'DOT' }
];

const DOCUMENT_PART_OPTIONS = [
  { value: 'full', label: 'Full Document' },
  { value: 'front', label: 'Front' },
  { value: 'back', label: 'Back' }
];

const VERIFICATION_STATUS_OPTIONS = [
  { value: '', label: 'Auto Detect' },
  { value: 'pending', label: 'Awaiting Review' },
  { value: 'verified', label: 'Verified' },
  { value: 'rejected', label: 'Rejected' }
];

const DOC_TYPES_WITH_PARTS = new Set(['drivers_license', 'twic', 'hazmat', 'cdl']);

const STATUS_LABELS = {
  valid: 'Valid',
  expiring_soon: 'Expiring Soon',
  expired: 'Expired',
  invalid: 'Invalid',
  missing: 'Missing',
  needs_review: 'Needs Review',
  action_required: 'Action Required',
  ready: 'Ready',
  completed: 'Completed',
  eligible: 'Eligible',
  restricted: 'Restricted',
  pending: 'Awaiting Review',
  verified: 'Verified',
  rejected: 'Rejected'
};

const statusCache = new Map();
const complianceRequestCache = new Map();
const optimisticUploadCache = new Map();
const DRIVER_COMPLIANCE_BADGE_CACHE_TTL_MS = 2 * 60 * 1000;
const documentPreviewCache = new Map();
const DRIVER_CREDENTIAL_TYPES = new Set(CREDENTIAL_DOCUMENT_DEFINITIONS.map(option => option.type));
const DRIVER_COMPLIANCE_REQUEST_TIMEOUT_MS = 10000;

const panelStyles = {
  container: {
    border: '1px solid rgba(255, 255, 255, 0.12)',
    borderRadius: '12px',
    padding: '16px',
    background: 'rgba(39, 41, 61, 0.55)',
    width: '100%',
  },
  loading: {
    color: 'rgba(255, 255, 255, 0.78)',
  },
  error: {
    color: '#ffb3b3',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '14px',
    flexWrap: 'wrap',
  },
  heading: {
    marginBottom: '4px',
  },
  copy: {
    marginBottom: 0,
    fontSize: '0.92rem',
    color: 'rgba(255, 255, 255, 0.72)',
  },
  statusBadgeBase: {
    borderRadius: '999px',
    padding: '8px 14px',
    fontSize: '0.82rem',
    fontWeight: 700,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  },
  metaGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '12px',
    marginBottom: '14px',
  },
  metaCard: {
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '10px',
    padding: '12px',
    background: 'rgba(255, 255, 255, 0.03)',
  },
  sectionTitle: {
    marginBottom: '10px',
  },
  documentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
    gap: '14px',
    marginBottom: '16px',
  },
  documentCardBase: {
    borderRadius: '12px',
    overflow: 'hidden',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    background: 'rgba(255, 255, 255, 0.04)',
    minHeight: '280px',
    display: 'flex',
    flexDirection: 'column',
  },
  documentLabel: {
    padding: '10px 12px 0',
    fontWeight: 700,
    fontSize: '0.95rem',
  },
  documentPreview: {
    margin: '10px 12px 8px',
    minHeight: '190px',
    borderRadius: '10px',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(17, 17, 17, 0.35)',
    border: '1px dashed rgba(255, 255, 255, 0.12)',
    color: 'rgba(255, 255, 255, 0.78)',
    textAlign: 'center',
    padding: '12px',
  },
  documentImage: {
    width: '100%',
    height: '190px',
    objectFit: 'contain',
    objectPosition: 'center top',
    display: 'block',
  },
  documentMeta: {
    padding: '0 12px 12px',
    display: 'grid',
    gap: '6px',
    fontSize: '0.88rem',
  },
  statusLine: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
  },
  statusPillBase: {
    borderRadius: '999px',
    padding: '4px 10px',
    fontSize: '0.76rem',
    fontWeight: 700,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  placeholderText: {
    fontSize: '0.88rem',
    color: 'rgba(255, 255, 255, 0.7)',
  },
  linkButton: {
    marginTop: '4px',
  },
  alertBase: {
    borderRadius: '10px',
    padding: '10px 12px',
    marginBottom: '12px',
    fontSize: '0.92rem',
  },
  assetsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '12px',
    marginBottom: '16px',
  },
  dispatchMetaStrip: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: '12px',
    marginBottom: '12px',
  },
  dispatchMetaCard: {
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '12px',
    padding: '12px',
    background: 'rgba(255, 255, 255, 0.03)',
    display: 'grid',
    gap: '6px',
  },
  dispatchGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: '12px',
    alignItems: 'start',
  },
  dispatchColumn: {
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '12px',
    padding: '12px',
    background: 'rgba(255, 255, 255, 0.03)',
    minHeight: '100%',
  },
  dispatchAsideCard: {
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '12px',
    padding: '12px',
    background: 'rgba(255, 255, 255, 0.03)',
    minHeight: '100%',
  },
  compactDocumentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(205px, 1fr))',
    gap: '10px',
  },
  requiredDocumentsLayout: {
    display: 'grid',
    gap: '10px',
  },
  requiredDocumentsSecondaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '10px',
  },
  requiredCompactCard: {
    borderRadius: '10px',
    overflow: 'hidden',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    background: 'rgba(255, 255, 255, 0.04)',
    display: 'grid',
    gap: '10px',
    padding: '12px',
    minHeight: '100%',
  },
  requiredCompactHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '10px',
    flexWrap: 'wrap',
  },
  vehicleInfoCard: {
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '10px',
    padding: '10px 12px',
    background: 'rgba(255, 255, 255, 0.02)',
    marginBottom: '10px',
    display: 'grid',
    gap: '4px',
  },
  vehicleSelectorCard: {
  border: '1px solid rgba(255, 255, 255, 0.08)',
  borderRadius: '10px',
  padding: '12px',
  background: 'rgba(255, 255, 255, 0.03)',
  display: 'grid',
  gap: '10px',
  marginBottom: '12px',
  },
  vehicleSelectorHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
    flexWrap: 'wrap',
  },
  vehicleSelectorCopy: {
    fontSize: '0.82rem',
    color: 'rgba(255, 255, 255, 0.66)',
    marginBottom: 0,
  },
  vehicleSelectionGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(220px, 280px) minmax(0, 1fr)',
    gap: '12px',
    alignItems: 'start',
  },
  vehicleAssociationCopy: {
    fontSize: '0.76rem',
    color: 'rgba(255, 255, 255, 0.68)',
    lineHeight: 1.3,
  },
  vehicleAssociationBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '2px 8px',
    borderRadius: '999px',
    fontSize: '0.74rem',
    fontWeight: 600,
    border: '1px solid rgba(255, 255, 255, 0.12)',
    background: 'rgba(255, 255, 255, 0.05)',
    color: 'rgba(255, 255, 255, 0.82)',
  },
  subSection: {
    marginTop: '10px',
  },
  disableReason: {
    fontSize: '0.86rem',
    color: '#ffd189',
  },
  assetCard: {
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '10px',
    padding: '10px 12px',
    background: 'rgba(255, 255, 255, 0.03)',
  },
  compactDocumentCard: {
    borderRadius: '10px',
    overflow: 'hidden',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    background: 'rgba(255, 255, 255, 0.04)',
    display: 'grid',
    gridTemplateColumns: '86px minmax(0, 1fr)',
    minHeight: '92px',
  },
  featuredDocumentCard: {
    borderRadius: '10px',
    overflow: 'hidden',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    background: 'rgba(255, 255, 255, 0.04)',
    display: 'grid',
    gap: '10px',
    padding: '12px',
  },
  featuredDocumentHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '8px',
    flexWrap: 'wrap',
  },
  featuredDocumentMeta: {
    display: 'grid',
    gap: '6px',
    fontSize: '0.84rem',
    minWidth: 0,
  },
  featuredDocumentExpiry: {
    display: 'grid',
    gap: '2px',
    fontSize: '0.8rem',
  },
  documentControlStack: {
    display: 'grid',
    gap: '6px',
    justifyItems: 'end',
    textAlign: 'right',
    flexShrink: 0,
    minWidth: 0,
    maxWidth: '100%',
  },
  documentExpiryStack: {
    display: 'grid',
    gap: '2px',
    fontSize: '0.8rem',
    color: 'rgba(255, 255, 255, 0.92)',
  },
  documentControlLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 10px',
    lineHeight: 1.25,
    borderRadius: '999px',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    background: 'rgba(255, 255, 255, 0.06)',
    color: '#ffffff',
    cursor: 'pointer',
    fontSize: '0.76rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  featuredPreview: {
    minHeight: '280px',
    margin: 0,
    padding: '8px',
  },
  featuredImage: {
    width: '100%',
    height: '264px',
    objectFit: 'contain',
    objectPosition: 'center top',
    display: 'block',
  },
  compactPreview: {
    minHeight: '92px',
    height: '100%',
    margin: 0,
    borderRadius: 0,
    border: 0,
    borderRight: '1px dashed rgba(255, 255, 255, 0.12)',
    padding: 0,
  },
  compactImage: {
    width: '100%',
    height: '100%',
    minHeight: '76px',
    objectFit: 'contain',
    objectPosition: 'center top',
    display: 'block',
  },
  requiredCompactPreview: {
    minHeight: '220px',
    margin: 0,
    padding: '8px',
  },
  requiredCompactImage: {
    width: '100%',
    height: '204px',
    objectFit: 'contain',
    objectPosition: 'center top',
    display: 'block',
  },
  compactDocumentBody: {
    padding: '8px 10px',
    display: 'grid',
    gap: '6px',
    alignContent: 'start',
  },
  compactDocumentHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '8px',
  },
  compactDocumentLabel: {
    fontWeight: 700,
    fontSize: '0.88rem',
    lineHeight: 1.2,
  },
  compactDocumentMeta: {
    display: 'grid',
    gap: '4px',
    fontSize: '0.8rem',
  },
  compactExpiryLabel: {
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    color: 'rgba(255, 255, 255, 0.56)',
  },
  compactExpiryValue: {
    fontSize: '0.84rem',
    color: 'rgba(255, 255, 255, 0.92)',
    lineHeight: 1.3,
  },
  previewButton: {
    display: 'block',
    width: '100%',
    padding: 0,
    margin: 0,
    background: 'transparent',
    border: 0,
    cursor: 'pointer',
    textAlign: 'inherit',
  },
  compactUploadLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '2px',
    padding: '2px 8px',
    lineHeight: 1.25,
    borderRadius: '999px',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    background: 'rgba(255, 255, 255, 0.06)',
    color: '#ffffff',
    cursor: 'pointer',
    fontSize: '0.76rem',
    fontWeight: 600,
    marginRight: '6px',
  },
  compactActionRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    alignItems: 'center',
  },
  dispatchBodySection: {
    display: 'grid',
    gap: '10px',
  },
  dispatchEditorPanel: {
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '10px',
    padding: '12px',
    background: 'rgba(255, 255, 255, 0.02)',
    display: 'grid',
    gap: '12px',
  },
  dispatchEditorHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
  },
  dispatchAlert: {
    borderRadius: '10px',
    padding: '8px 10px',
    fontSize: '0.84rem',
  },
  dispatchActionButton: {
    padding: '4px 10px',
    lineHeight: 1.2,
    width: 'auto',
    minWidth: 'unset',
  },
  dispatchStack: {
    display: 'grid',
    gap: '10px',
  },
  emptyStateCard: {
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '10px',
    padding: '10px 12px',
    background: 'rgba(255, 255, 255, 0.03)',
    color: 'rgba(255, 255, 255, 0.72)',
    fontSize: '0.88rem',
  },
  editor: {
    marginTop: '4px',
  },
  editorGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '12px',
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '0.85rem',
    marginBottom: '6px',
  },
  assetsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  },
  assetRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
    gap: '12px',
    marginBottom: '12px',
    padding: '12px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '10px',
  },
  assetRemove: {
    display: 'flex',
    alignItems: 'flex-end',
  },
  actionsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginTop: '10px',
    flexWrap: 'wrap',
  },
  successCopy: {
    color: '#8ce6b1',
  },
  checkboxGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '10px',
    marginBottom: '16px',
  },
  checkboxCard: {
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '10px',
    padding: '10px 12px',
    background: 'rgba(255, 255, 255, 0.03)',
    display: 'grid',
    gap: '8px',
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: 0,
    fontSize: '0.88rem',
  },
  onboardingPreview: {
    display: 'grid',
    gridTemplateColumns: '92px minmax(0, 1fr)',
    gap: '12px',
    alignItems: 'center',
    padding: '12px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '12px',
    background: 'rgba(255, 255, 255, 0.03)',
    marginBottom: '16px',
  },
  onboardingPreviewImage: {
    width: '92px',
    height: '92px',
    borderRadius: '16px',
    objectFit: 'cover',
    display: 'block',
    border: '1px solid rgba(255, 255, 255, 0.12)',
  },
  guidanceList: {
    margin: 0,
    paddingLeft: '18px',
    color: 'rgba(255, 255, 255, 0.78)',
    fontSize: '0.88rem',
    display: 'grid',
    gap: '4px',
  },
  uploadGrid: {
    display: 'grid',
    gap: '12px',
    marginBottom: '16px',
  },
  uploadRow: {
    display: 'grid',
    gap: '10px',
    padding: '12px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '12px',
    background: 'rgba(255, 255, 255, 0.03)',
  },
  uploadHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  uploadMetaGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '10px',
  },
  uploadHint: {
    fontSize: '0.82rem',
    color: 'rgba(255, 255, 255, 0.66)',
  },
  quickPickerRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: '10px',
  },
  quickPickerSelect: {
    minWidth: '150px',
    maxWidth: '220px',
  },
  hiddenInput: {
    display: 'none',
  },
  quickUploadCard: {
    cursor: 'pointer',
    transition: 'transform 120ms ease, border-color 120ms ease, background 120ms ease',
  },
  quickUploadCopy: {
    fontSize: '0.74rem',
    color: 'rgba(255, 255, 255, 0.68)',
  },
  inlineMessage: {
    fontSize: '0.8rem',
    color: 'rgba(255, 255, 255, 0.72)',
  },
  reviewPanel: {
    display: 'grid',
    gap: '8px',
    marginTop: '10px',
    padding: '10px',
    borderRadius: '10px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    background: 'rgba(255, 255, 255, 0.03)',
  },
  reviewInputGrid: {
    display: 'grid',
    gap: '8px',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
  },
  reviewActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    alignItems: 'center',
  },
  reviewHint: {
    fontSize: '0.78rem',
    color: 'rgba(255, 255, 255, 0.66)',
    marginBottom: 0,
  },
};

const createEmptyAsset = vehicleId => ({
  asset_id: `new-${Date.now()}-${Math.random()}`,
  vehicle_id: vehicleId || '',
  asset_type: 'bumper_pull',
  length_feet: '',
  description: ''
});

const createEmptyOnboardingDraft = () => ({
  onboarding_tier: 'non_dot',
  required_credentials: [],
  require_profile_photo: true,
  require_twic_photo: false,
  require_hazmat_photo: false,
  driver_visibility_enabled: true,
  completed_onboarding: false,
});

const getStatusColors = status => {
  switch (status) {
    case 'valid':
    case 'eligible':
      return { background: 'rgba(46, 204, 113, 0.18)', color: '#8ce6b1', border: 'rgba(46, 204, 113, 0.25)' };
    case 'expiring_soon':
      return { background: 'rgba(243, 156, 18, 0.18)', color: '#ffd189', border: 'rgba(243, 156, 18, 0.3)' };
    case 'missing':
    case 'expired':
    case 'needs_review':
    case 'action_required':
    case 'restricted':
      return { background: 'rgba(231, 76, 60, 0.18)', color: '#ffb3aa', border: 'rgba(231, 76, 60, 0.3)' };
    case 'completed':
      return { background: 'rgba(46, 204, 113, 0.18)', color: '#8ce6b1', border: 'rgba(46, 204, 113, 0.25)' };
    case 'ready':
      return { background: 'rgba(52, 152, 219, 0.18)', color: '#9fd9ff', border: 'rgba(52, 152, 219, 0.28)' };
    default:
      return { background: 'rgba(255, 255, 255, 0.08)', color: '#ffffff', border: 'rgba(255, 255, 255, 0.12)' };
  }
};

const formatStatus = value => STATUS_LABELS[value] || value || 'Unknown';

const parseDateInputToUtcMs = value => {
  if (!value) {
    return null;
  }

  const parts = String(value).split('-').map(part => Number(part));
  if (parts.length !== 3 || parts.some(part => !Number.isInteger(part))) {
    return null;
  }

  const [year, month, day] = parts;
  return Date.UTC(year, month - 1, day);
};

const parseExpirationValueToUtcMs = value => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = Number(value);
  if (Number.isFinite(numericValue)) {
    return numericValue;
  }

  const normalizedValue = String(value).trim();
  const inputStyleValue = parseDateInputToUtcMs(normalizedValue);
  if (inputStyleValue != null) {
    return inputStyleValue;
  }

  const parsedMs = new Date(normalizedValue).getTime();
  return Number.isFinite(parsedMs) ? parsedMs : null;
};

const formatDate = value => {
  if (!value) {
    return 'Awaiting review';
  }

  const parsed = new Date(Number(value));
  if (Number.isNaN(parsed.getTime())) {
    return 'Awaiting review';
  }

  return parsed.toLocaleDateString('en-US', { timeZone: 'UTC' });
};

const formatDateToInputValue = value => {
  if (!value) {
    return '';
  }

  const numValue = Number(value);
  if (!Number.isFinite(numValue)) {
    return '';
  }

  const parsed = new Date(numValue);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  const year = parsed.getUTCFullYear();
  const month = String(parsed.getUTCMonth() + 1).padStart(2, '0');
  const day = String(parsed.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const normalizeExpirationValue = value => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : value;
};

const getDocumentExpirationValue = document => {
  if (!document) {
    return null;
  }

  if (document.document_expires_at !== null && document.document_expires_at !== undefined && document.document_expires_at !== '') {
    return normalizeExpirationValue(document.document_expires_at);
  }

  const metaExpiration = document.ocr_text && document.ocr_text.meta
    ? document.ocr_text.meta.document_expires_at
    : null;

  if (metaExpiration !== null && metaExpiration !== undefined && metaExpiration !== '') {
    return normalizeExpirationValue(metaExpiration);
  }

  if (
    document.signed_url_expires_at !== null &&
    document.signed_url_expires_at !== undefined &&
    document.expires_at !== null &&
    document.expires_at !== undefined &&
    String(document.expires_at) === String(document.signed_url_expires_at)
  ) {
    return null;
  }

  return normalizeExpirationValue(document.expires_at);
};

const normalizeDocumentExpiration = document => {
  if (!document || typeof document !== 'object') {
    return document;
  }

  const expiresAt = getDocumentExpirationValue(document);
  const reviewMeta = document.ocr_text && document.ocr_text.meta && typeof document.ocr_text.meta === 'object'
    ? document.ocr_text.meta
    : {};
  const invalidReasons = Array.isArray(reviewMeta.invalid_reasons)
    ? reviewMeta.invalid_reasons
    : (Array.isArray(document.invalid_reasons) ? document.invalid_reasons : []);
  const verificationStatus = reviewMeta.verification_status || document.verification_status || '';
  const ocrError = reviewMeta.ocr_error !== undefined
    ? reviewMeta.ocr_error
    : (document.ocr_error !== undefined ? document.ocr_error : null);

  return {
    ...document,
    expires_at: expiresAt,
    document_expires_at: expiresAt,
    invalid_reasons: invalidReasons,
    verification_status: verificationStatus,
    ocr_error: ocrError,
  };
};

const normalizeDocumentType = value => {
  const normalizedValue = String(value || '').trim().toLowerCase();

  if (normalizedValue === 'insurance_card') {
    return 'proof_of_insurance';
  }

  return normalizedValue;
};

const getNormalizedDocumentType = document => normalizeDocumentType(document && (document.type || document.doc_type));

const documentHasResolvedExpiration = document => Boolean(
  document
  && getDocumentExpirationValue(document)
  && !document.ocr_error
  && !(Array.isArray(document.invalid_reasons) && document.invalid_reasons.length > 0)
);

const normalizeDocumentStatus = document => {
  if (!document || typeof document !== 'object') {
    return document;
  }

  if (document.status && document.status !== 'needs_review' && document.status !== 'invalid') {
    return document;
  }

  if (document.verification_status === 'pending') {
    return {
      ...document,
      status: documentHasResolvedExpiration(document) ? 'valid' : 'needs_review',
    };
  }

  if (document.verification_status === 'rejected') {
    return {
      ...document,
      status: 'invalid',
    };
  }

  if (document.verification_status === 'verified' || document.verification_status === 'approved') {
    return {
      ...document,
      status: documentHasResolvedExpiration(document) ? 'valid' : 'needs_review',
    };
  }

  if (document.signed_url || document.file_name) {
    return {
      ...document,
      status: documentHasResolvedExpiration(document) ? 'valid' : 'needs_review',
    };
  }

  return {
    ...document,
    status: 'missing',
  };
};

const normalizeDocumentList = documents => (Array.isArray(documents)
  ? documents.map(document => {
      const normalizedDocument = normalizeDocumentExpiration(document);
      const canonicalType = getNormalizedDocumentType(normalizedDocument);

      return normalizeDocumentStatus({
        ...normalizedDocument,
        type: canonicalType,
        doc_type: canonicalType,
      });
    })
  : []);

const hasDraftExpirationValue = draft => Boolean(
  draft && Object.prototype.hasOwnProperty.call(draft, 'document_expires_at')
);

const buildReviewDraft = (document, existingDraft = {}) => {
  if (hasDraftExpirationValue(existingDraft)) {
    return existingDraft;
  }

  if (!documentApprovalNeedsExpiration(document)) {
    return existingDraft;
  }

  const ocrExpiration = document && document.ocr_text && document.ocr_text.meta
    ? document.ocr_text.meta.document_expires_at
    : null;

  if (!ocrExpiration) {
    return existingDraft;
  }

  return {
    ...existingDraft,
    document_expires_at: formatDateToInputValue(ocrExpiration),
    _ocr_prepopulated: true,
  };
};

const buildExpiryEditDraft = (document, existingDraft = {}) => {
  if (hasDraftExpirationValue(existingDraft)) {
    return existingDraft;
  }

  const currentExpiration = getDocumentExpirationValue(document);
  if (!currentExpiration) {
    return existingDraft;
  }

  return {
    ...existingDraft,
    document_expires_at: formatDateToInputValue(currentExpiration),
  };
};

const normalizeCompliancePayload = payload => {
  if (!payload || typeof payload !== 'object') {
    return payload;
  }

  const compliance = payload.compliance || {};
  const onboarding = compliance.onboarding || {};

  return {
    ...payload,
    compliance: {
      ...compliance,
      required_documents: normalizeDocumentList(compliance.required_documents),
      vehicle_required_documents: Array.isArray(compliance.vehicle_required_documents)
        ? compliance.vehicle_required_documents.map(vehicleDocuments => ({
            ...vehicleDocuments,
            documents: normalizeDocumentList(vehicleDocuments && vehicleDocuments.documents),
          }))
        : [],
      credential_documents: normalizeDocumentList(compliance.credential_documents),
      onboarding: compliance.onboarding
        ? {
            ...onboarding,
            required_documents: normalizeDocumentList(onboarding.required_documents),
            required_credentials: normalizeDocumentList(onboarding.required_credentials),
            required_photos: normalizeDocumentList(onboarding.required_photos),
            expiring_documents: normalizeDocumentList(onboarding.expiring_documents),
          }
        : compliance.onboarding,
    },
  };
};

const getDocumentPreviewCacheKey = document => {
  if (!document || !document.signed_url) {
    return '';
  }

  return [document.updated_at, document.created_at, getDocumentExpirationValue(document), document.file_name]
    .filter(value => value !== null && value !== undefined && value !== '')
    .map(value => String(value))
    .join(':');
};

const isPresignedDocumentUrl = url => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  return /[?&](X-Goog-Algorithm|X-Goog-Signature|X-Amz-Algorithm|X-Amz-Signature)=/i.test(url);
};

const getDocumentStorageType = document => {
  if (!document) {
    return '';
  }

  if (document.doc_type) {
    return document.doc_type;
  }

  if (document.type === 'proof_of_insurance') {
    return 'insurance_card';
  }

  return document.type || '';
};

const buildDocumentPreviewLookupKey = (driverAuth0Id, document) => {
  if (!driverAuth0Id || !document || !document.file_name) {
    return '';
  }

  return [
    driverAuth0Id,
    document.vehicle_id || '',
    getDocumentStorageType(document),
    document.file_name,
  ].join(':');
};

const getDocumentPreviewUrl = document => {
  if (!document || !document.signed_url) {
    return '';
  }

  if (isPresignedDocumentUrl(document.signed_url)) {
    return document.signed_url;
  }

  const cacheKey = getDocumentPreviewCacheKey(document);
  if (!cacheKey) {
    return document.signed_url;
  }

  const separator = document.signed_url.includes('?') ? '&' : '?';
  return `${document.signed_url}${separator}preview_key=${encodeURIComponent(cacheKey)}`;
};

const getExpirationCopy = document => {
  if (document.status === 'missing') {
    return 'Missing information';
  }

  const expiresAt = getDocumentExpirationValue(document);

  if (!expiresAt) {
    if (document.signed_url || document.file_name) {
      return document.verification_status === 'pending' ? 'Awaiting review' : 'Uploaded';
    }

    return 'Awaiting review';
  }

  return formatDate(expiresAt);
};

const getVerificationCopy = document => {
  if (!document || (!document.signed_url && !document.file_name)) {
    return 'Not uploaded';
  }

  if (document.verification_status === 'rejected') {
    return 'Rejected';
  }

  if (document.status === 'valid' || document.status === 'expiring_soon') {
    return 'Valid';
  }

  if (document.status === 'expired') {
    return 'Expired';
  }

  if (document.ocr_error || (document.invalid_reasons && document.invalid_reasons.length > 0) || document.status === 'needs_review' || document.status === 'invalid') {
    return 'Awaiting review';
  }

  if (!document.verification_status || document.verification_status === 'pending') {
    return 'Awaiting review';
  }

  return formatStatus(document.verification_status);
};

const getVehicleSummary = primaryVehicle => {
  if (!primaryVehicle) {
    return 'Not set';
  }

  const details = [
    primaryVehicle.year,
    primaryVehicle.make,
    primaryVehicle.model,
    primaryVehicle.color,
  ].filter(Boolean).join(' ');

  if (details) {
    return details;
  }

  return primaryVehicle.vehicle_label || primaryVehicle.vehicle_classification || 'Not set';
};

const getVehicleTypeLabel = vehicleType => {
  const matchedOption = VEHICLE_OPTIONS.find(option => option.value === vehicleType);
  return matchedOption ? matchedOption.label : (vehicleType || 'N/A');
};

const getVehicleOptionLabel = vehicle => {
  if (!vehicle) {
    return 'Vehicle';
  }

  const summary = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ');
  const typeLabel = getVehicleTypeLabel(vehicle.vehicle_classification || vehicle.type);
  const plateValue = vehicle.plate_value ? `Plate ${vehicle.plate_value}` : '';

  return [summary || 'Vehicle', typeLabel !== 'N/A' ? typeLabel : '', plateValue].filter(Boolean).join(' • ');
};

const isVehicleDocumentType = documentType => {
  const normalizedType = normalizeDocumentType(documentType);
  return normalizedType === 'proof_of_insurance' || normalizedType === 'vehicle_registration';
};

const isVehicleScopedDocument = document => {
  if (!document) {
    return false;
  }

  return document.source === 'vehicle' || isVehicleDocumentType(document.type || document.doc_type);
};

const buildDocumentTargetKey = (document, selectedVehicleId = '') => {
  const source = document && document.source ? document.source : (isVehicleDocumentType(document && (document.type || document.doc_type)) ? 'vehicle' : 'user');
  const type = getNormalizedDocumentType(document) || 'unknown';
  const vehicleId = source === 'vehicle' ? ((document && document.vehicle_id) || selectedVehicleId || 'selected-vehicle') : '';

  return source === 'vehicle'
    ? `${source}:${type}:${vehicleId}`
    : `${source}:${type}`;
};

const attachVehicleContextToDocument = (document, vehicles = []) => {
  if (!document) {
    return document;
  }

  if (!isVehicleScopedDocument(document)) {
    return document;
  }

  const linkedVehicle = (vehicles || []).find(vehicle => vehicle.vehicle_id === document.vehicle_id) || null;

  return {
    ...document,
    source: document.source || 'vehicle',
    vehicle_display_label: linkedVehicle ? getVehicleOptionLabel(linkedVehicle) : (document.vehicle_display_label || 'Selected vehicle'),
  };
};

const extractPreviewExtension = document => {
  const source = document.file_name || document.signed_url || '';
  const cleaned = source.split('?')[0].toLowerCase();

  if (cleaned.endsWith('.png') || cleaned.endsWith('.jpg') || cleaned.endsWith('.jpeg') || cleaned.endsWith('.webp') || cleaned.endsWith('.gif') || cleaned.endsWith('.bmp') || cleaned.endsWith('.heic')) {
    return 'image';
  }

  if (cleaned.endsWith('.pdf')) {
    return 'pdf';
  }

  return document.signed_url ? 'file' : 'missing';
};

const buildDocumentCollection = (documents, definitions, { selectedVehicleId = '', vehicles = [] } = {}) => {
  return definitions.map(definition => {
    const definitionType = normalizeDocumentType(definition.type);
    const matchingDocuments = (documents || []).filter(document => document && getNormalizedDocumentType(document) === definitionType);
    const isVehicleDocument = isVehicleDocumentType(definition.type);
    let existing = null;

    if (isVehicleDocument) {
      existing = matchingDocuments.find(document => document.vehicle_id && document.vehicle_id === selectedVehicleId)
        || matchingDocuments.find(document => !document.vehicle_id && selectedVehicleId)
        || matchingDocuments[0]
        || null;
    } else {
      existing = matchingDocuments[0] || null;
    }

    if (existing) {
      return attachVehicleContextToDocument({
        ...existing,
        label: existing.label || definition.label,
      }, vehicles);
    }

    return attachVehicleContextToDocument({
      type: definition.type,
      doc_type: definition.type,
      label: definition.label,
      source: isVehicleDocument ? 'vehicle' : 'user',
      status: 'missing',
      expires_at: null,
      signed_url: '',
      file_name: '',
      vehicle_id: isVehicleDocument ? (selectedVehicleId || '') : '',
    }, vehicles);
  });
};

const hydrateOnboardingDraft = nextPayload => {
  const onboarding = nextPayload && nextPayload.compliance && nextPayload.compliance.onboarding
    ? nextPayload.compliance.onboarding
    : null;
  const storedProfile = nextPayload && nextPayload.driver && nextPayload.driver.restricted_area_credentials && typeof nextPayload.driver.restricted_area_credentials === 'object'
    ? nextPayload.driver.restricted_area_credentials
    : {};

  return {
    onboarding_tier: onboarding && onboarding.tier ? onboarding.tier : 'non_dot',
    required_credentials: onboarding && Array.isArray(onboarding.required_credentials)
      ? onboarding.required_credentials.map(document => document.type)
      : [],
    require_profile_photo: storedProfile.require_profile_photo !== false,
    require_twic_photo: Boolean(storedProfile.require_twic_photo),
    require_hazmat_photo: Boolean(storedProfile.require_hazmat_photo),
    driver_visibility_enabled: !(onboarding && onboarding.driver_visibility && onboarding.driver_visibility.enabled === false),
    completed_onboarding: Boolean(nextPayload && nextPayload.driver && nextPayload.driver.completed_onboarding),
  };
};

const getUniqueDocuments = documents => {
  const seen = new Set();
  return (documents || []).filter(document => {
    const key = `${document.type}:${document.vehicle_id || ''}:${document.file_name || document.label || ''}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

const sortRequiredDocuments = documents => (documents || []).slice().sort((left, right) => {
  const leftOrder = Object.prototype.hasOwnProperty.call(REQUIRED_DOCUMENT_ORDER, left && left.type)
    ? REQUIRED_DOCUMENT_ORDER[left.type]
    : Number.MAX_SAFE_INTEGER;
  const rightOrder = Object.prototype.hasOwnProperty.call(REQUIRED_DOCUMENT_ORDER, right && right.type)
    ? REQUIRED_DOCUMENT_ORDER[right.type]
    : Number.MAX_SAFE_INTEGER;

  if (leftOrder !== rightOrder) {
    return leftOrder - rightOrder;
  }

  return String((left && left.label) || '').localeCompare(String((right && right.label) || ''));
});

const buildQuickUploadDefaults = documentType => ({
  document_part: DOC_TYPES_WITH_PARTS.has(documentType) ? 'front' : 'full',
  verification_status: '',
  document_expires_at: '',
});

const normalizePhoneNumber = phoneNumber => {
  if (!phoneNumber) {
    return '';
  }

  const trimmed = String(phoneNumber).trim();
  if (trimmed.startsWith('+')) {
    return trimmed;
  }

  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === 10) {
    return `+1${digits}`;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }

  return digits ? `+${digits}` : '';
};

const getReminderDocumentItems = sections => sections
  .flatMap(section => section || [])
  .filter(document => document && document.type)
  .filter(document => ['missing', 'expired', 'needs_review', 'expiring_soon'].includes(document.status));

const buildReminderMessage = ({ driverName, documents }) => {
  const intro = driverName ? `Hi ${driverName},` : 'Hi,';

  if (!documents || documents.length === 0) {
    return `${intro} please review your driver profile and confirm your onboarding information is current in Gophr.`;
  }

  const summary = documents.map(document => `${document.label} (${formatStatus(document.status).toLowerCase()})`).join(', ');
  return `${intro} please update your Gophr onboarding items: ${summary}. Open the driver profile and upload the latest documents.`;
};

const buildDocumentReviewKey = document => [
  document && document.source ? document.source : 'user',
  document && document.type ? document.type : '',
  document && document.vehicle_id ? document.vehicle_id : '',
  document && document.file_name ? document.file_name : '',
].join(':');

const documentNeedsManualReview = document => {
  if (!document || (!document.signed_url && !document.file_name)) {
    return false;
  }

  return document.status === 'needs_review'
    || document.status === 'invalid'
    || document.verification_status === 'pending'
    || document.verification_status === 'rejected';
};

// Lift gate is a vehicle accessory/capability, not a dated document — it never expires.
const NON_EXPIRING_DOCUMENT_TYPES = new Set(['profile_photo', 'lift_gate']);

const documentApprovalNeedsExpiration = document => {
  return Boolean(document)
    && !NON_EXPIRING_DOCUMENT_TYPES.has(normalizeDocumentType(document.type || document.doc_type))
    && !getDocumentExpirationValue(document);
};

const buildDeniedDocumentMessage = ({ driverName, documentLabel, reviewNote }) => {
  const intro = driverName ? `Hi ${driverName},` : 'Hi,';
  const detail = reviewNote ? ` Reason: ${reviewNote}.` : '';

  return `${intro} your ${documentLabel} was denied during document review. Please upload an updated version in Gophr.${detail}`;
};

const canEditDocumentExpiration = document => Boolean(
  document
  && !NON_EXPIRING_DOCUMENT_TYPES.has(normalizeDocumentType(document.type || document.doc_type))
  && (document.signed_url || document.file_name)
);

// Allow editing the expiration date on any document that has a file (valid or expired),
// except those still awaiting manual review (they use the review panel's own date field).
const shouldShowExpiryEditor = document => canEditDocumentExpiration(document)
  && !documentNeedsManualReview(document);

const buildUploadTargets = ({ requiredDocumentCards, onboardingRequiredDocuments, onboardingCredentialCards, onboardingPhotoCards, onboardingExpiringCards, selectedVehicleId }) => {
  const seen = new Set();
  return requiredDocumentCards
    .concat(onboardingRequiredDocuments)
    .concat(onboardingCredentialCards)
    .concat(onboardingPhotoCards)
    .concat(onboardingExpiringCards)
    .filter(document => {
      if (!document || !document.type) {
        return false;
      }

      const key = buildDocumentTargetKey(document, selectedVehicleId);
      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    })
    .map(document => {
      const source = document.source || (document.type === 'proof_of_insurance' || document.type === 'vehicle_registration' ? 'vehicle' : 'user');
      return {
        key: buildDocumentTargetKey(document, selectedVehicleId),
        type: document.type,
        doc_type: document.doc_type || document.type,
        label: document.label,
        source,
        vehicle_id: source === 'vehicle' ? (document.vehicle_id || selectedVehicleId || '') : '',
      };
    });
};

const getSelectedVehicleRequiredDocuments = (compliance, selectedVehicleId) => {
  if (!compliance || !Array.isArray(compliance.vehicle_required_documents)) {
    return [];
  }

  const matchedVehicleDocuments = compliance.vehicle_required_documents.find(vehicleDocuments => vehicleDocuments && vehicleDocuments.vehicle_id === selectedVehicleId)
    || compliance.vehicle_required_documents.find(vehicleDocuments => vehicleDocuments && !vehicleDocuments.vehicle_id && Array.isArray(vehicleDocuments.documents) && vehicleDocuments.documents.length > 0)
    || compliance.vehicle_required_documents.find(vehicleDocuments => vehicleDocuments && Array.isArray(vehicleDocuments.documents) && vehicleDocuments.documents.length > 0);

  return matchedVehicleDocuments && Array.isArray(matchedVehicleDocuments.documents)
    ? matchedVehicleDocuments.documents.map(document => ({
        ...document,
        vehicle_id: document && document.vehicle_id ? document.vehicle_id : ((matchedVehicleDocuments && matchedVehicleDocuments.vehicle_id) || selectedVehicleId || ''),
      }))
    : [];
};

const getVehicleDocumentStatusScore = status => {
  switch (status) {
    case 'valid':
      return 5;
    case 'expiring_soon':
      return 4;
    case 'needs_review':
      return 2;
    case 'invalid':
      return 1;
    case 'expired':
      return 0;
    case 'missing':
    default:
      return -1;
  }
};

const resolveInitialVehicleId = ({ payload, vehicles, dispatchVehicleId = '' }) => {
  const availableVehicles = Array.isArray(vehicles) ? vehicles.filter(Boolean) : [];
  if (availableVehicles.length === 0) {
    return dispatchVehicleId || '';
  }

  const availableVehicleIds = new Set(availableVehicles.map(vehicle => vehicle.vehicle_id));
  const requestedVehicleIds = [
    payload && payload.driver ? payload.driver.active_vehicle : '',
    payload && payload.compliance && payload.compliance.primary_vehicle ? payload.compliance.primary_vehicle.vehicle_id : '',
    dispatchVehicleId,
  ].filter(Boolean);

  const directlyMatchedVehicleId = requestedVehicleIds.find(vehicleId => availableVehicleIds.has(vehicleId));
  if (directlyMatchedVehicleId) {
    return directlyMatchedVehicleId;
  }

  const vehicleRequiredDocuments = payload && payload.compliance && Array.isArray(payload.compliance.vehicle_required_documents)
    ? payload.compliance.vehicle_required_documents
    : [];
  let bestVehicleId = availableVehicles[0].vehicle_id;
  let bestScore = Number.NEGATIVE_INFINITY;
  let bestUploads = -1;

  vehicleRequiredDocuments.forEach(entry => {
    if (!entry || !availableVehicleIds.has(entry.vehicle_id)) {
      return;
    }

    const documents = Array.isArray(entry.documents) ? entry.documents : [];
    const score = documents.reduce((total, document) => total + getVehicleDocumentStatusScore(document && document.status), 0);
    const uploads = documents.reduce((total, document) => total + (document && (document.signed_url || document.file_name) ? 1 : 0), 0);

    if (score > bestScore || (score === bestScore && uploads > bestUploads)) {
      bestVehicleId = entry.vehicle_id;
      bestScore = score;
      bestUploads = uploads;
    }
  });

  return bestVehicleId;
};

const clonePayload = payload => {
  if (!payload) {
    return payload;
  }

  return JSON.parse(JSON.stringify(payload));
};

const buildOptimisticDocument = ({ existingDocument, target, uploadResult, fallbackFile }) => {
  const isProfilePhoto = target.type === 'profile_photo';
  const optimisticUrl = uploadResult && uploadResult.url ? uploadResult.url : (existingDocument && existingDocument.signed_url) || null;
  const optimisticFileName = uploadResult && uploadResult.file_name ? uploadResult.file_name : (fallbackFile && fallbackFile.name) || (existingDocument && existingDocument.file_name) || null;
  const optimisticExpiration = uploadResult && Object.prototype.hasOwnProperty.call(uploadResult, 'document_expires_at')
    ? uploadResult.document_expires_at
    : ((existingDocument && existingDocument.expires_at) || null);

  return {
    ...(existingDocument || {}),
    type: target.type,
    doc_type: (existingDocument && existingDocument.doc_type) || target.doc_type || target.type,
    label: (existingDocument && existingDocument.label) || target.label,
    source: target.source,
    status: isProfilePhoto ? 'valid' : ((existingDocument && existingDocument.status && existingDocument.status !== 'missing') ? existingDocument.status : 'needs_review'),
    updated_at: (uploadResult && (uploadResult.updated_at || uploadResult.updatedAt)) || new Date().toISOString(),
    created_at: (existingDocument && (existingDocument.created_at || existingDocument.createdAt)) || null,
    signed_url: optimisticUrl,
    file_name: optimisticFileName,
    expires_at: optimisticExpiration,
    verification_status: (uploadResult && uploadResult.verification_status) || (existingDocument && existingDocument.verification_status) || (isProfilePhoto ? 'verified' : 'pending'),
    document_part: (uploadResult && uploadResult.document_part) || (existingDocument && existingDocument.document_part) || 'full',
    invalid_reasons: [],
    vehicle_id: target.source === 'vehicle'
      ? ((uploadResult && uploadResult.vehicle_id) || (existingDocument && existingDocument.vehicle_id) || target.vehicle_id || null)
      : ((existingDocument && existingDocument.vehicle_id) || null),
  };
};

const patchDocumentList = (documents, target, uploadResult, fallbackFile) => {
  const list = Array.isArray(documents) ? documents : [];
  let didPatch = false;
  const nextList = list.map(document => {
    if ((document && document.type) !== target.type) {
      return document;
    }

    if (target && target.source === 'vehicle') {
      const currentVehicleId = document && document.vehicle_id ? document.vehicle_id : '';
      const targetVehicleId = target.vehicle_id || '';

      if (currentVehicleId && targetVehicleId && currentVehicleId !== targetVehicleId) {
        return document;
      }
    }

    didPatch = true;
    return buildOptimisticDocument({ existingDocument: document, target, uploadResult, fallbackFile });
  });

  if (!didPatch) {
    nextList.push(buildOptimisticDocument({ existingDocument: null, target, uploadResult, fallbackFile }));
  }

  return nextList;
};

const applyOptimisticUploadToPayload = (currentPayload, target, uploadResult, fallbackFile) => {
  const nextPayload = clonePayload(currentPayload);
  if (!nextPayload || !nextPayload.compliance) {
    return currentPayload;
  }

  nextPayload.compliance.required_documents = patchDocumentList(nextPayload.compliance.required_documents, target, uploadResult, fallbackFile);
  nextPayload.compliance.credential_documents = patchDocumentList(nextPayload.compliance.credential_documents, target, uploadResult, fallbackFile);

  if (nextPayload.compliance.onboarding) {
    nextPayload.compliance.onboarding.required_documents = patchDocumentList(nextPayload.compliance.onboarding.required_documents, target, uploadResult, fallbackFile);
    nextPayload.compliance.onboarding.required_credentials = patchDocumentList(nextPayload.compliance.onboarding.required_credentials, target, uploadResult, fallbackFile);
    nextPayload.compliance.onboarding.required_photos = patchDocumentList(nextPayload.compliance.onboarding.required_photos, target, uploadResult, fallbackFile);

    if (target.type === 'profile_photo' && uploadResult && uploadResult.url) {
      if (!nextPayload.compliance.onboarding.driver_visibility) {
        nextPayload.compliance.onboarding.driver_visibility = { enabled: true, profile_photo_url: uploadResult.url };
      } else {
        nextPayload.compliance.onboarding.driver_visibility.profile_photo_url = uploadResult.url;
      }
    }
  }

  if (target.type === 'profile_photo' && nextPayload.driver && uploadResult && uploadResult.url) {
    nextPayload.driver.profile_photo_url = uploadResult.url;
  }

  return nextPayload;
};

const applyOptimisticEntriesToPayload = (currentPayload, optimisticEntries) => {
  let nextPayload = currentPayload;
  const entries = optimisticEntries && typeof optimisticEntries === 'object' ? Object.values(optimisticEntries) : [];

  entries.forEach(entry => {
    if (!entry || !entry.target || !entry.uploadResult) {
      return;
    }

    nextPayload = applyOptimisticUploadToPayload(nextPayload, entry.target, entry.uploadResult, entry.fallbackFile || null);
  });

  return nextPayload;
};

const getAuthHeaders = () => ({
  Authorization: 'Bearer ' + localStorage.getItem('access_token'),
  'content-type': 'application/json'
});

const resolveDriverAdminIdentifier = (driverAuth0Id, driverIdentifier = null) => driverAuth0Id || driverIdentifier || '';

const buildDispatchSnapshotComplianceStatus = driverSnapshot => {
  if (!driverSnapshot) {
    return 'restricted';
  }

  if (driverSnapshot.compliance_status) {
    return driverSnapshot.compliance_status;
  }

  if (driverSnapshot.banned_by_branch || driverSnapshot.status === 'inactive') {
    return 'restricted';
  }

  return driverSnapshot.completed_onboarding ? 'eligible' : 'restricted';
};

const buildDispatchSnapshotPayload = ({ driverAuth0Id, lookupIdentifier, dispatchContext = null }) => {
  const driverSnapshot = dispatchContext && dispatchContext.driverSnapshot ? dispatchContext.driverSnapshot : null;

  if (!driverSnapshot) {
    return null;
  }

  const rawUser = driverSnapshot.User || {};
  const vehicles = Array.isArray(driverSnapshot.Vehicles) ? driverSnapshot.Vehicles.filter(Boolean) : [];
  const primaryVehicle = dispatchContext && dispatchContext.vehicle
    ? dispatchContext.vehicle
    : (vehicles.find(vehicle => vehicle.vehicle_id === driverSnapshot.active_vehicle) || vehicles[0] || null);
  const normalizedUserDocuments = Array.isArray(rawUser.UserDocuments)
    ? rawUser.UserDocuments.filter(Boolean).map(document => ({
        ...document,
        source: document.source || 'user',
        type: document.type || document.doc_type || '',
      }))
    : [];
  const vehicleRequiredDocuments = vehicles.map(vehicle => ({
    vehicle_id: vehicle.vehicle_id,
    documents: Array.isArray(vehicle.VehicleDocuments)
      ? vehicle.VehicleDocuments.filter(Boolean).map(document => ({
          ...document,
          source: document.source || 'vehicle',
          vehicle_id: document.vehicle_id || vehicle.vehicle_id,
          type: document.type || document.doc_type || '',
        }))
      : [],
  }));
  const flattenedVehicleDocuments = vehicleRequiredDocuments.flatMap(group => group.documents || []);
  const credentialDocuments = normalizedUserDocuments.filter(document => DRIVER_CREDENTIAL_TYPES.has(document.type || document.doc_type));
  const profilePhotoDocuments = normalizedUserDocuments.filter(document => (document.type || document.doc_type) === 'profile_photo');
  const requiredDocuments = normalizedUserDocuments.filter(document => (document.type || document.doc_type) === 'drivers_license');
  const onboardingRequiredDocuments = normalizedUserDocuments
    .filter(document => {
      const documentType = document.type || document.doc_type;
      return documentType && documentType !== 'profile_photo' && !DRIVER_CREDENTIAL_TYPES.has(documentType);
    })
    .concat(flattenedVehicleDocuments);
  const complianceStatus = buildDispatchSnapshotComplianceStatus(driverSnapshot);

  return normalizeCompliancePayload({
    driver: {
      driver_id: driverSnapshot.driver_id || null,
      auth0_id: driverSnapshot.auth0_id || driverAuth0Id || lookupIdentifier || null,
      user_id: driverSnapshot.user_id || null,
      active_vehicle: (primaryVehicle && primaryVehicle.vehicle_id) || driverSnapshot.active_vehicle || '',
      carrying_capacity: driverSnapshot.carrying_capacity || null,
      employment_type: driverSnapshot.employment_type || null,
      status: driverSnapshot.status || 'active',
      branch_id: dispatchContext && dispatchContext.branch_id ? dispatchContext.branch_id : null,
      profile_photo_url: rawUser.profile_photo_url || driverSnapshot.profile_photo_url || null,
      completed_onboarding: Boolean(driverSnapshot.completed_onboarding),
      restricted_area_credentials: driverSnapshot.restricted_area_credentials || [],
      banned_by_branch: Boolean(driverSnapshot.banned_by_branch),
      load_board_disabled: false,
      disable_reasons: [],
      user: {
        firstname: rawUser.firstname || driverSnapshot.firstname || '',
        lastname: rawUser.lastname || driverSnapshot.lastname || '',
        email: rawUser.email || driverSnapshot.email || '',
        phone: rawUser.phone || driverSnapshot.phone || '',
      },
    },
    vehicles,
    assets: [],
    compliance: {
      compliance_status: complianceStatus,
      credential_status: driverSnapshot.credential_status || (complianceStatus === 'eligible' ? 'valid' : 'expired'),
      eligible_for_loads: driverSnapshot.eligible_for_loads !== false && complianceStatus === 'eligible',
      primary_vehicle: primaryVehicle,
      required_documents: requiredDocuments,
      credential_documents: credentialDocuments,
      valid_credentials: credentialDocuments.filter(document => ['valid', 'expiring_soon'].includes(document.status)).map(document => document.label || document.type || document.doc_type),
      blocking_reasons: [],
      warnings: ['Showing dispatch snapshot while detailed compliance sync finishes.'],
      vehicle_required_documents: vehicleRequiredDocuments,
      onboarding: {
        status: complianceStatus === 'eligible' ? 'ready' : 'action_required',
        required_documents: onboardingRequiredDocuments,
        required_credentials: credentialDocuments,
        required_photos: profilePhotoDocuments,
        expiring_documents: normalizedUserDocuments.concat(flattenedVehicleDocuments).filter(document => document && document.expires_at),
        driver_visibility: {
          enabled: true,
          profile_photo_url: rawUser.profile_photo_url || driverSnapshot.profile_photo_url || null,
        },
      },
    },
  });
};

const fetchCompliancePayload = async (driverIdentifier, { force = false } = {}) => {
  if (!driverIdentifier) {
    return null;
  }

  const cachedPayload = readCachedPayload(driverIdentifier);
  if (!force && cachedPayload) {
    return cachedPayload;
  }

  const cachedRequest = complianceRequestCache.get(driverIdentifier);
  if (!force && cachedRequest) {
    return cachedRequest;
  }

  const request = (async () => {
    try {
      try {
        const response = await requestDriverAdmin({
          driverIdentifier,
          suffix: '/onboarding',
          params: force ? { _ts: Date.now() } : undefined,
        });

        const normalizedPayload = normalizeCompliancePayload(response.data.payload);
        cachePayloadStatus(driverIdentifier, normalizedPayload);
        return normalizedPayload;
      } catch (err) {
        const status = err && err.response ? err.response.status : null;
        if (status && status !== 404 && status !== 403) {
          throw err;
        }

        const response = await requestDriverAdmin({
          driverIdentifier,
          suffix: '/compliance',
          params: force ? { _ts: Date.now() } : undefined,
        });

        const normalizedPayload = normalizeCompliancePayload(response.data.payload);
        cachePayloadStatus(driverIdentifier, normalizedPayload);
        return normalizedPayload;
      }
    } finally {
      complianceRequestCache.delete(driverIdentifier);
    }
  })();

  complianceRequestCache.set(driverIdentifier, request);
  return request;
};

const BADGE_COMPLIANCE_FETCH_CONCURRENCY = 6;
const badgeComplianceFetchQueue = [];
let activeBadgeComplianceFetches = 0;

const drainBadgeComplianceFetchQueue = () => {
  if (activeBadgeComplianceFetches >= BADGE_COMPLIANCE_FETCH_CONCURRENCY || badgeComplianceFetchQueue.length === 0) {
    return;
  }

  const queuedFetch = badgeComplianceFetchQueue.shift();
  activeBadgeComplianceFetches += 1;

  fetchCompliancePayload(queuedFetch.driverIdentifier, { force: true })
    .then(queuedFetch.resolve)
    .catch(queuedFetch.reject)
    .finally(() => {
      activeBadgeComplianceFetches -= 1;
      drainBadgeComplianceFetchQueue();
    });

  drainBadgeComplianceFetchQueue();
};

const fetchBadgeCompliancePayload = driverIdentifier => new Promise((resolve, reject) => {
  badgeComplianceFetchQueue.push({ driverIdentifier, resolve, reject });
  drainBadgeComplianceFetchQueue();
});

const readCachedStatus = driverAuth0Id => {
  const cached = statusCache.get(driverAuth0Id);
  return cached ? cached.compliance_status : null;
};

const hasFreshCachedStatus = (driverAuth0Id, maxAgeMs = DRIVER_COMPLIANCE_BADGE_CACHE_TTL_MS) => {
  const cached = statusCache.get(driverAuth0Id);

  if (!cached || typeof cached.cached_at !== 'number') {
    return false;
  }

  return Date.now() - cached.cached_at <= maxAgeMs;
};

const readCachedPayload = driverAuth0Id => {
  const cached = statusCache.get(driverAuth0Id);
  return cached ? cached.payload : null;
};

const cachePayloadStatus = (driverAuth0Id, payload) => {
  if (!driverAuth0Id || !payload || !payload.compliance) {
    return;
  }

  statusCache.set(driverAuth0Id, {
    compliance_status: payload.compliance.compliance_status,
    cached_at: Date.now(),
    payload,
  });
};

const readCachedOptimisticUploads = driverIdentifier => {
  if (!driverIdentifier) {
    return {};
  }

  return optimisticUploadCache.get(driverIdentifier) || {};
};

const cacheOptimisticUploads = (driverIdentifier, optimisticUploads) => {
  if (!driverIdentifier) {
    return;
  }

  if (!optimisticUploads || Object.keys(optimisticUploads).length === 0) {
    optimisticUploadCache.delete(driverIdentifier);
    return;
  }

  optimisticUploadCache.set(driverIdentifier, optimisticUploads);
};

const ValidStatusPill = ({ status }) => {
  const colors = getStatusColors(status);
  const isValid = status === 'valid' || status === 'eligible' || status === 'expiring_soon';

  return (
    <span style={{ ...panelStyles.statusPillBase, background: colors.background, color: colors.color, border: `1px solid ${colors.border}` }}>
      <span>{isValid ? '✓' : '✕'}</span>
      <span>{formatStatus(status)}</span>
    </span>
  );
};

  const DocumentCard = ({ document, driverAuth0Id = '', driverIdentifier = '', compact = false, layout = 'compact', uploadAction = null, uploadMessage = '', uploadBusy = false, uploadInputId = '', reviewState = null, onReviewDraftChange = null, onReviewAction = null, expiryEditState = null, onExpiryDraftChange = null, onExpirySave = null }) => {
  const previewType = extractPreviewExtension(document);
  const colors = getStatusColors(document.status);
  const fallbackPreviewUrl = getDocumentPreviewUrl(document);
  const vehicleContextCopy = document && document.vehicle_display_label ? document.vehicle_display_label : '';
  const [previewUrl, setPreviewUrl] = useState(() => {
    const lookupKey = buildDocumentPreviewLookupKey(driverAuth0Id, document);
    return (lookupKey && documentPreviewCache.get(lookupKey)) || fallbackPreviewUrl;
  });

  useEffect(() => {
    let cancelled = false;
    const lookupKey = buildDocumentPreviewLookupKey(driverAuth0Id, document);
    setPreviewUrl((lookupKey && documentPreviewCache.get(lookupKey)) || fallbackPreviewUrl);

    if (!lookupKey || !driverAuth0Id || !document.file_name) {
      return () => {
        cancelled = true;
      };
    }

    const documentType = getDocumentStorageType(document);
    const lookupDriverIdentifier = resolveDriverAdminIdentifier(driverAuth0Id, driverIdentifier);

    if (!lookupDriverIdentifier || !documentType) {
      return () => {
        cancelled = true;
      };
    }

    requestDriverAdmin({
      driverIdentifier: lookupDriverIdentifier,
      suffix: '/document-preview',
      params: {
        source: document.source || 'user',
        doc_type: documentType,
        file_name: document.file_name,
        vehicle_id: document.vehicle_id || '',
      },
    }).then(response => {
      const resolvedUrl = response && response.data && response.data.payload && response.data.payload.url
        ? response.data.payload.url
        : '';
      if (!resolvedUrl) {
        return;
      }

      documentPreviewCache.set(lookupKey, resolvedUrl);
      if (!cancelled) {
        setPreviewUrl(resolvedUrl);
      }
    })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [driverAuth0Id, driverIdentifier, document, document.file_name, document.vehicle_id, document.source, fallbackPreviewUrl]);

  const openDocument = () => {
    const targetUrl = previewUrl || document.signed_url;
    if (targetUrl) {
      window.open(targetUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const renderUploadControl = (labelStyle = panelStyles.compactUploadLabel) => {
    if (!(uploadAction && uploadInputId)) {
      return null;
    }

    return (
      <>
        <label htmlFor={uploadInputId} style={labelStyle}>
          {uploadBusy ? 'Uploading...' : document.signed_url ? 'Update Photo' : 'Upload Photo'}
        </label>
        <input
          id={uploadInputId}
          type="file"
          accept="application/pdf,image/*"
          style={panelStyles.hiddenInput}
          onChange={event => {
            const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
            if (file) {
              uploadAction(file);
            }
            event.target.value = '';
          }}
          disabled={uploadBusy}
        />
      </>
    );
  };

  const renderPreviewContent = imageStyle => {
    if (previewType === 'image') {
      return <img src={previewUrl} alt={document.label} style={imageStyle} />;
    }

    if (previewType === 'pdf') {
      return (
        <div>
          <div style={{ fontSize: layout === 'featured' ? '2rem' : '1.15rem', fontWeight: 700, marginBottom: '4px' }}>PDF</div>
          <div style={{ ...panelStyles.placeholderText, fontSize: layout === 'featured' ? '0.82rem' : '0.74rem' }}>Click preview to open</div>
        </div>
      );
    }

    if (previewType === 'file') {
      return <div style={{ ...panelStyles.placeholderText, fontSize: layout === 'featured' ? '0.82rem' : '0.74rem' }}>Click preview to open</div>;
    }

    return (
      <div>
        <div style={{ fontSize: layout === 'featured' ? '1.1rem' : '1rem', fontWeight: 700, marginBottom: '4px' }}>Missing</div>
        <div style={{ ...panelStyles.placeholderText, fontSize: layout === 'featured' ? '0.82rem' : '0.74rem' }}>No upload</div>
      </div>
    );
  };

  const renderPreview = (previewStyle, imageStyle) => {
    const previewInner = (
      <div style={{ ...panelStyles.documentPreview, ...previewStyle, borderColor: colors.border }}>
        {renderPreviewContent(imageStyle)}
      </div>
    );

    if (previewUrl || document.signed_url) {
      return (
        <button type="button" style={panelStyles.previewButton} onClick={openDocument} aria-label={`Open ${document.label}`}>
          {previewInner}
        </button>
      );
    }

    return previewInner;
  };

  const renderReviewPanel = () => {
    if (!reviewState || !onReviewAction || !documentNeedsManualReview(document)) {
      return null;
    }

    const nextDraft = reviewState.draft || {};
    const reviewMessage = reviewState.message || '';
    const reviewBusy = Boolean(reviewState.busy);
    const needsExpiration = documentApprovalNeedsExpiration(document);

    return (
      <div style={panelStyles.reviewPanel}>
        <p style={panelStyles.reviewHint}>
          {needsExpiration
            ? 'This document needs manual review. The expiration date has been extracted via OCR. Adjust if needed and approve, or deny with a reason.'
            : 'This document needs manual review. Approve it or deny it with a driver-facing reason.'}
        </p>
        <div style={panelStyles.reviewInputGrid}>
          <div>
            <label style={panelStyles.label}>
              {nextDraft._ocr_prepopulated ? 'Expiration Date (OCR Extracted)' : 'Expiry Override'}
            </label>
            <Input
              type="date"
              value={nextDraft.document_expires_at || ''}
              onChange={event => onReviewDraftChange({ document_expires_at: event.target.value, _ocr_prepopulated: false })}
              disabled={reviewBusy}
              title={nextDraft._ocr_prepopulated ? 'Date extracted from document via OCR. Edit if needed.' : undefined}
            />
          </div>
          <div>
            <label style={panelStyles.label}>Denial Reason</label>
            <Input
              type="textarea"
              rows="3"
              value={nextDraft.review_note || ''}
              onChange={event => onReviewDraftChange({ review_note: event.target.value })}
              disabled={reviewBusy}
              placeholder="Tell the driver what needs to be fixed"
            />
          </div>
        </div>
        <div style={panelStyles.reviewActions}>
          <Button type="button" className="btn btn-success btn-sm" onClick={() => onReviewAction('approve')} disabled={reviewBusy}>
            {reviewBusy ? 'Saving...' : 'Approve'}
          </Button>
          <Button type="button" className="btn btn-danger btn-sm" onClick={() => onReviewAction('deny')} disabled={reviewBusy}>
            {reviewBusy ? 'Saving...' : 'Deny And Message Driver'}
          </Button>
          {reviewMessage ? <span style={panelStyles.inlineMessage}>{reviewMessage}</span> : null}
        </div>
      </div>
    );
  };

  const renderExpiryEditPanel = () => {
    if (!expiryEditState || !onExpiryDraftChange || !onExpirySave) {
      return null;
    }

    const nextDraft = expiryEditState.draft || {};
    const expiryMessage = expiryEditState.message || '';
    const expiryBusy = Boolean(expiryEditState.busy);

    return (
      <div style={panelStyles.reviewPanel}>
        <p style={panelStyles.reviewHint}>
          Fix the expiration date here if the stored date is wrong, or upload a replacement document below.
        </p>
        <div style={panelStyles.reviewInputGrid}>
          <div>
            <label style={panelStyles.label}>Correct Expiration Date</label>
            <Input
              type="date"
              value={nextDraft.document_expires_at || ''}
              onChange={event => onExpiryDraftChange({ document_expires_at: event.target.value })}
              disabled={expiryBusy}
            />
          </div>
        </div>
        <div style={panelStyles.reviewActions}>
          <Button type="button" className="btn btn-success btn-sm" onClick={onExpirySave} disabled={expiryBusy}>
            {expiryBusy ? 'Saving...' : 'Save Expiration'}
          </Button>
          {expiryMessage ? <span style={panelStyles.inlineMessage}>{expiryMessage}</span> : null}
        </div>
      </div>
    );
  };

  if (layout === 'featured') {
    return (
      <div style={{ ...panelStyles.featuredDocumentCard, borderColor: colors.border }}>
        <div style={panelStyles.featuredDocumentHeader}>
          <div style={panelStyles.featuredDocumentMeta}>
            <div style={panelStyles.compactDocumentLabel}>{document.label}</div>
            {vehicleContextCopy ? <div style={panelStyles.vehicleAssociationCopy}>{vehicleContextCopy}</div> : null}
            {(document.verification_status || document.signed_url || document.file_name) ? <div><strong>Verification:</strong> {getVerificationCopy(document)}</div> : null}
            {document.invalid_reasons && document.invalid_reasons.length > 0 ? <div><strong>Issues:</strong> {document.invalid_reasons.join(', ')}</div> : null}
          </div>
          <div style={panelStyles.documentControlStack}>
            <ValidStatusPill status={document.status} />
            <div style={panelStyles.documentExpiryStack}>
              <strong>Expiration Date</strong>
              <span>{getExpirationCopy(document)}</span>
            </div>
            {renderUploadControl(panelStyles.documentControlLabel)}
          </div>
        </div>
        {renderPreview(panelStyles.featuredPreview, panelStyles.featuredImage)}
        {uploadMessage ? <div style={panelStyles.quickUploadCopy}>{uploadMessage}</div> : null}
        {renderExpiryEditPanel()}
        {renderReviewPanel()}
      </div>
    );
  }

  if (layout === 'required-compact') {
    return (
      <div style={{ ...panelStyles.requiredCompactCard, borderColor: colors.border }}>
        <div style={panelStyles.requiredCompactHeader}>
          <div style={panelStyles.featuredDocumentMeta}>
            <div style={panelStyles.compactDocumentLabel}>{document.label}</div>
            {vehicleContextCopy ? <div style={panelStyles.vehicleAssociationCopy}>{vehicleContextCopy}</div> : null}
            {(document.verification_status || document.signed_url || document.file_name) ? <div><strong>Verification:</strong> {getVerificationCopy(document)}</div> : null}
            {document.invalid_reasons && document.invalid_reasons.length > 0 ? <div><strong>Issues:</strong> {document.invalid_reasons.join(', ')}</div> : null}
          </div>
          <div style={panelStyles.documentControlStack}>
            <ValidStatusPill status={document.status} />
            <div style={panelStyles.documentExpiryStack}>
              <strong>Expiration Date</strong>
              <span>{getExpirationCopy(document)}</span>
            </div>
            {renderUploadControl(panelStyles.documentControlLabel)}
          </div>
        </div>
        {renderPreview(panelStyles.requiredCompactPreview, panelStyles.requiredCompactImage)}
        {uploadMessage ? <div style={panelStyles.quickUploadCopy}>{uploadMessage}</div> : null}
        {renderExpiryEditPanel()}
        {renderReviewPanel()}
      </div>
    );
  }

  if (compact) {
    return (
      <div style={{ ...panelStyles.compactDocumentCard, ...((uploadAction || uploadBusy) ? panelStyles.quickUploadCard : {}), borderColor: colors.border }}>
        {renderPreview(panelStyles.compactPreview, panelStyles.compactImage)}
        <div style={panelStyles.compactDocumentBody}>
          <div style={panelStyles.compactDocumentHeader}>
            <div>
              <div style={panelStyles.compactDocumentLabel}>{document.label}</div>
              {vehicleContextCopy ? <div style={panelStyles.vehicleAssociationCopy}>{vehicleContextCopy}</div> : null}
            </div>
            <ValidStatusPill status={document.status} />
          </div>
          <div style={panelStyles.compactDocumentMeta}>
            <div style={panelStyles.compactExpiryLabel}>Expiration Date</div>
            <div style={panelStyles.compactExpiryValue}>{getExpirationCopy(document)}</div>
            {document.invalid_reasons && document.invalid_reasons.length > 0 ? <div><strong>Issues:</strong> {document.invalid_reasons.join(', ')}</div> : null}
            <div style={panelStyles.compactActionRow}>
              {renderUploadControl()}
            </div>
            {uploadMessage ? <div style={panelStyles.quickUploadCopy}>{uploadMessage}</div> : null}
            {renderExpiryEditPanel()}
            {renderReviewPanel()}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...panelStyles.documentCardBase, borderColor: colors.border }}>
      <div style={panelStyles.documentLabel}>{document.label}</div>
      {vehicleContextCopy ? <div style={{ padding: '0 12px', ...panelStyles.vehicleAssociationCopy }}>{vehicleContextCopy}</div> : null}
      <div style={{ ...panelStyles.documentPreview, borderColor: colors.border }}>
        {previewType === 'image' ? (
          <img src={previewUrl} alt={document.label} style={panelStyles.documentImage} />
        ) : previewType === 'pdf' ? (
          <div>
            <div style={{ fontSize: '2rem', marginBottom: '8px' }}>PDF</div>
            <div style={panelStyles.placeholderText}>Preview in new tab</div>
          </div>
        ) : previewType === 'file' ? (
          <div style={panelStyles.placeholderText}>File available for download</div>
        ) : (
          <div>
            <div style={{ fontSize: '1.8rem', marginBottom: '8px' }}>Missing</div>
            <div style={panelStyles.placeholderText}>Missing information</div>
          </div>
        )}
      </div>
      <div style={panelStyles.documentMeta}>
        <div style={panelStyles.statusLine}>
          <span>Status</span>
          <ValidStatusPill status={document.status} />
        </div>
        <div style={panelStyles.statusLine}>
          <span>Expires</span>
          <strong>{getExpirationCopy(document)}</strong>
        </div>
        {(document.verification_status || document.signed_url || document.file_name) ? (
          <div style={panelStyles.statusLine}>
            <span>Verification</span>
            <strong>{getVerificationCopy(document)}</strong>
          </div>
        ) : null}
        {document.invalid_reasons && document.invalid_reasons.length > 0 ? (
          <div><strong>Issues:</strong> {document.invalid_reasons.join(', ')}</div>
        ) : null}
        {(uploadAction && uploadInputId) || uploadMessage ? (
          <div style={panelStyles.compactActionRow}>
            {renderUploadControl()}
            {uploadMessage ? <div style={panelStyles.quickUploadCopy}>{uploadMessage}</div> : null}
          </div>
        ) : null}
      </div>
      {renderExpiryEditPanel()}
      {renderReviewPanel()}
    </div>
  );
};

export const DriverComplianceValidBadge = ({ driverAuth0Id, driverIdentifier = null, initialStatus = null, shouldFetch = true }) => {
  const lookupIdentifier = resolveDriverAdminIdentifier(driverAuth0Id, driverIdentifier);
  const badgeRef = useRef(null);
  const usedSyntheticFallbackRef = useRef(false);
  const [status, setStatus] = useState(() => readCachedStatus(lookupIdentifier) || initialStatus || null);
  const [shouldLoadStatus, setShouldLoadStatus] = useState(false);

  useEffect(() => {
    if (!shouldFetch || status || typeof IntersectionObserver === 'undefined') {
      setShouldLoadStatus(Boolean(shouldFetch && !status));
      return undefined;
    }

    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        setShouldLoadStatus(true);
        observer.disconnect();
      }
    }, { rootMargin: '300px 0px' });

    if (badgeRef.current) {
      observer.observe(badgeRef.current);
    }

    return () => observer.disconnect();
  }, [shouldFetch, status]);

  useEffect(() => {
    const cachedStatus = readCachedStatus(lookupIdentifier);

    if (cachedStatus) {
      usedSyntheticFallbackRef.current = false;
      setStatus(cachedStatus);
      return;
    }

    if (!shouldFetch && initialStatus) {
      usedSyntheticFallbackRef.current = false;
      setStatus(initialStatus);
    }
  }, [initialStatus, lookupIdentifier, shouldFetch]);

  useEffect(() => {
    let isMounted = true;

    if (!lookupIdentifier || !shouldLoadStatus) {
      return () => {
        isMounted = false;
      };
    }

    const cachedStatus = readCachedStatus(lookupIdentifier);
    if (cachedStatus) {
      usedSyntheticFallbackRef.current = false;
      setStatus(cachedStatus);

      if (!shouldFetch || hasFreshCachedStatus(lookupIdentifier)) {
        return () => {
          isMounted = false;
        };
      }
    }

    if (!shouldFetch) {
      if (isMounted && initialStatus) {
        usedSyntheticFallbackRef.current = false;
        setStatus(initialStatus);
      }

      return () => {
        isMounted = false;
      };
    }

    fetchBadgeCompliancePayload(lookupIdentifier)
      .then(payload => {
        cachePayloadStatus(lookupIdentifier, payload);
        if (isMounted && payload && payload.compliance) {
          usedSyntheticFallbackRef.current = false;
          setStatus(payload.compliance.compliance_status);
        }
      })
      .catch(() => {
        if (isMounted) {
          usedSyntheticFallbackRef.current = true;
          setStatus('unknown');
        }
      });

    return () => {
      isMounted = false;
    };
  }, [initialStatus, lookupIdentifier, shouldFetch, shouldLoadStatus]);

  if (!status) {
    return <span ref={badgeRef} style={{ color: 'rgba(255, 255, 255, 0.75)' }}>Checking...</span>;
  }

  const colors = getStatusColors(status);
  const isValid = status === 'eligible';
  const isUnknown = status === 'unknown';
  const statusLabel = isUnknown ? 'Unknown' : (isValid ? 'Valid' : 'Action Required');

  return (
    <span ref={badgeRef} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: isUnknown ? 'rgba(255, 255, 255, 0.75)' : colors.color, fontWeight: 700 }}>
      <span>{isUnknown ? '?' : (isValid ? '✓' : '✕')}</span>
      <span>{statusLabel}</span>
    </span>
  );
};

const DriverCompliancePanel = ({ driverAuth0Id, driverIdentifier = null, variant = 'default', showEditor = true, headerAside = null, dispatchContext = null }) => {
  const lookupIdentifier = resolveDriverAdminIdentifier(driverAuth0Id, driverIdentifier);
  const dispatchDriverSnapshot = dispatchContext && dispatchContext.driverSnapshot ? dispatchContext.driverSnapshot : null;
  const dispatchVehicle = dispatchContext && dispatchContext.vehicle ? dispatchContext.vehicle : null;
  const dispatchBranchId = dispatchContext && dispatchContext.branch_id ? dispatchContext.branch_id : null;
  const dispatchSnapshotPayload = useMemo(
    () => buildDispatchSnapshotPayload({
      driverAuth0Id,
      lookupIdentifier,
      dispatchContext: {
        branch_id: dispatchBranchId,
        driverSnapshot: dispatchDriverSnapshot,
        vehicle: dispatchVehicle,
      },
    }),
    [dispatchBranchId, dispatchDriverSnapshot, dispatchVehicle, driverAuth0Id, lookupIdentifier]
  );
  const dispatchVehicleId = dispatchVehicle && dispatchVehicle.vehicle_id
    ? dispatchVehicle.vehicle_id
    : '';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadBoardSaving, setLoadBoardSaving] = useState(false);
  const [error, setError] = useState('');
  const [saveMessage, setSaveMessage] = useState('');
  const [payload, setPayload] = useState(null);
  const [activeVehicleId, setActiveVehicleId] = useState('');
  const [vehicleClassification, setVehicleClassification] = useState('');
  const [assetsDraft, setAssetsDraft] = useState([]);
  const [onboardingDraft, setOnboardingDraft] = useState(createEmptyOnboardingDraft());
  const [reminderSending, setReminderSending] = useState(false);
  const [uploadDrafts, setUploadDrafts] = useState({});
  const [uploadingKeys, setUploadingKeys] = useState({});
  const [uploadMessages, setUploadMessages] = useState({});
  const [optimisticUploads, setOptimisticUploads] = useState(() => readCachedOptimisticUploads(lookupIdentifier));
  const [dispatchEditorOpen, setDispatchEditorOpen] = useState(false);
  const [newAssetType, setNewAssetType] = useState(ASSET_OPTIONS[0].value);
  const [selectedCredentialType, setSelectedCredentialType] = useState(CREDENTIAL_UPLOAD_OPTIONS[0].type);
  const [reviewDrafts, setReviewDrafts] = useState({});
  const [reviewingKeys, setReviewingKeys] = useState({});
  const [reviewMessages, setReviewMessages] = useState({});

  const syncPayload = (nextPayload, preserveForm = false, optimisticEntriesOverride = null) => {
    const mergedPayload = normalizeCompliancePayload(applyOptimisticEntriesToPayload(nextPayload, optimisticEntriesOverride || optimisticUploads));
    const nextVehicles = mergedPayload && mergedPayload.vehicles ? mergedPayload.vehicles : [];
    const nextActiveVehicleId = resolveInitialVehicleId({
      payload: mergedPayload,
      vehicles: nextVehicles,
      dispatchVehicleId,
    });
    const nextPrimaryVehicle = mergedPayload && mergedPayload.compliance ? mergedPayload.compliance.primary_vehicle : null;
    const nextSelectedVehicle = nextVehicles.find(vehicle => vehicle.vehicle_id === nextActiveVehicleId) || nextPrimaryVehicle || nextVehicles[0] || null;

    cachePayloadStatus(lookupIdentifier, mergedPayload);
    setPayload(mergedPayload);
    if (!preserveForm) {
      setActiveVehicleId(nextActiveVehicleId);
      setVehicleClassification(nextSelectedVehicle && nextSelectedVehicle.vehicle_classification ? nextSelectedVehicle.vehicle_classification : '');
      setAssetsDraft((mergedPayload && mergedPayload.assets ? mergedPayload.assets : []).map(asset => ({
        asset_id: asset.asset_id,
        vehicle_id: asset.vehicle_id || nextActiveVehicleId || dispatchVehicleId,
        asset_type: asset.asset_type,
        length_feet: asset.length_feet || '',
        description: asset.description || ''
      })));
      setOnboardingDraft(hydrateOnboardingDraft(mergedPayload));
    }
  };

  useEffect(() => {
    setOptimisticUploads(readCachedOptimisticUploads(lookupIdentifier));
  }, [lookupIdentifier]);

  useEffect(() => {
    cacheOptimisticUploads(lookupIdentifier, optimisticUploads);
  }, [lookupIdentifier, optimisticUploads]);

  useEffect(() => {
    let isMounted = true;
    const cachedPayload = readCachedPayload(lookupIdentifier);
    const bootstrapPayload = cachedPayload || dispatchSnapshotPayload || null;

    const loadCompliance = async preserveForm => {
      try {
        if (!preserveForm) {
          setLoading(true);
        }
        const nextPayload = await fetchCompliancePayload(lookupIdentifier, { force: true });
        if (isMounted) {
          syncPayload(nextPayload, preserveForm);
          setError('');
        }
      } catch (err) {
        if (isMounted) {
          const status = err && err.response ? err.response.status : null;
          if (status === 403 && bootstrapPayload) {
            setError('');
            return;
          }
          setError(err.response && err.response.data && err.response.data.message ? err.response.data.message : 'Unable to load compliance details');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (bootstrapPayload) {
      syncPayload(bootstrapPayload, false);
      setError('');
      setLoading(false);
    }

    loadCompliance(Boolean(bootstrapPayload));

    return () => {
      isMounted = false;
    };
  }, [dispatchSnapshotPayload, dispatchVehicleId, lookupIdentifier]);

  const vehicles = payload && payload.vehicles ? payload.vehicles : [];
  const availableVehicles = vehicles.length > 0
    ? vehicles
    : (dispatchContext && dispatchContext.vehicle ? [dispatchContext.vehicle] : []);
  const resolvedDriverAuth0Id = payload && payload.driver && payload.driver.auth0_id
    ? payload.driver.auth0_id
    : (driverAuth0Id || '');
  const selectedVehicleId = activeVehicleId || dispatchVehicleId || (vehicles[0] && vehicles[0].vehicle_id) || '';
  const selectedVehicle = availableVehicles.find(vehicle => vehicle.vehicle_id === selectedVehicleId) || null;
  const isDispatchVariant = variant === 'dispatch';
  const selectedVehicleRequiredDocuments = useMemo(
    () => getSelectedVehicleRequiredDocuments(payload && payload.compliance ? payload.compliance : null, selectedVehicleId),
    [payload, selectedVehicleId]
  );

  const requiredDocumentCards = useMemo(() => {
    if (!payload || !payload.compliance) {
      return buildDocumentCollection([], REQUIRED_DOCUMENT_DEFINITIONS, {
        selectedVehicleId,
        vehicles: availableVehicles,
      });
    }

    const globalRequiredDocuments = Array.isArray(payload.compliance.required_documents)
      ? payload.compliance.required_documents
      : [];
    const driverLicenseDocument = globalRequiredDocuments.find(document => document && document.type === 'drivers_license');
    const globalVehicleDocuments = globalRequiredDocuments.filter(document => document && isVehicleDocumentType(document.type || document.doc_type));
    const selectedVehicleDocuments = selectedVehicleRequiredDocuments.filter(document => document && isVehicleDocumentType(document.type || document.doc_type));
    const mergedRequiredDocuments = [driverLicenseDocument]
      .concat(selectedVehicleDocuments)
      .concat(globalVehicleDocuments)
      .filter(Boolean);

    return buildDocumentCollection(mergedRequiredDocuments, REQUIRED_DOCUMENT_DEFINITIONS, {
      selectedVehicleId,
      vehicles: availableVehicles,
    });
  }, [availableVehicles, payload, selectedVehicleId, selectedVehicleRequiredDocuments]);

  const credentialDocumentCards = useMemo(() => {
    if (!payload || !payload.compliance) {
      return [];
    }

    return (payload.compliance.credential_documents || []).map(document => {
      const definition = CREDENTIAL_DOCUMENT_DEFINITIONS.find(item => item.type === document.type);
      return {
        ...document,
        label: document.label || (definition ? definition.label : document.type)
      };
    });
  }, [payload]);

  const onboarding = payload && payload.compliance && payload.compliance.onboarding
    ? payload.compliance.onboarding
    : null;

  const onboardingRequiredDocuments = useMemo(() => getUniqueDocuments(onboarding && onboarding.required_documents ? onboarding.required_documents : []), [onboarding]);
  const onboardingCredentialCards = useMemo(() => getUniqueDocuments(onboarding && onboarding.required_credentials ? onboarding.required_credentials : []), [onboarding]);
  const onboardingPhotoCards = useMemo(() => getUniqueDocuments(onboarding && onboarding.required_photos ? onboarding.required_photos : []), [onboarding]);
  const onboardingExpiringCards = useMemo(() => getUniqueDocuments(onboarding && onboarding.expiring_documents ? onboarding.expiring_documents : []), [onboarding]);
  const uploadTargets = useMemo(() => buildUploadTargets({
    requiredDocumentCards,
    onboardingRequiredDocuments,
    onboardingCredentialCards,
    onboardingPhotoCards,
    onboardingExpiringCards,
    selectedVehicleId,
  }), [requiredDocumentCards, onboardingRequiredDocuments, onboardingCredentialCards, onboardingPhotoCards, onboardingExpiringCards, selectedVehicleId]);

  const uploadTargetsByKey = useMemo(() => uploadTargets.reduce((lookup, target) => {
    lookup[target.key] = target;
    return lookup;
  }, {}), [uploadTargets]);

  useEffect(() => {
    if (!selectedVehicleId) {
      setVehicleClassification('');
      return;
    }

    setVehicleClassification(selectedVehicle && selectedVehicle.vehicle_classification ? selectedVehicle.vehicle_classification : '');
  }, [selectedVehicleId, selectedVehicle]);

  const handleOnboardingFieldChange = (field, value) => {
    setOnboardingDraft(currentDraft => ({
      ...currentDraft,
      [field]: value,
    }));
  };

  const handleToggleCredentialRequirement = credentialType => {
    setOnboardingDraft(currentDraft => {
      const exists = currentDraft.required_credentials.includes(credentialType);
      return {
        ...currentDraft,
        required_credentials: exists
          ? currentDraft.required_credentials.filter(value => value !== credentialType)
          : currentDraft.required_credentials.concat(credentialType),
      };
    });
  };

  const handleUploadDraftChange = (targetKey, patch) => {
    setUploadDrafts(currentDrafts => ({
      ...currentDrafts,
      [targetKey]: {
        ...(currentDrafts[targetKey] || {}),
        ...patch,
      },
    }));
  };

  const handleReviewDraftChange = (reviewKey, patch) => {
    setReviewDrafts(currentDrafts => ({
      ...currentDrafts,
      [reviewKey]: {
        ...(currentDrafts[reviewKey] || {}),
        ...patch,
      },
    }));
  };

  const handleUploadDocument = async (target, overrides = {}) => {
    const draft = { ...(uploadDrafts[target.key] || {}), ...(overrides || {}) };
    const driverUploadAuth0Id = payload && payload.driver && payload.driver.auth0_id ? payload.driver.auth0_id : driverAuth0Id;
    const nextVehicleId = target.source === 'vehicle' ? (draft.vehicle_id || target.vehicle_id || selectedVehicleId) : null;
    const optimisticTarget = target.source === 'vehicle'
      ? {
          ...target,
          key: buildDocumentTargetKey({ ...target, vehicle_id: nextVehicleId || '' }, selectedVehicleId),
          vehicle_id: nextVehicleId || '',
        }
      : target;

    if (!driverUploadAuth0Id) {
      setError('Driver auth0 id is required before uploading documents.');
      return;
    }

    if (!draft.file) {
      setUploadMessages(currentMessages => ({
        ...currentMessages,
        [target.key]: 'Select a PDF or image file before uploading.',
      }));
      return;
    }

    if (target.source === 'vehicle' && !nextVehicleId) {
      setUploadMessages(currentMessages => ({
        ...currentMessages,
        [target.key]: 'Select an active vehicle before uploading vehicle documents.',
      }));
      return;
    }

    const formData = new FormData();
    formData.append('document', draft.file);
    if (draft.document_part) {
      formData.append('document_part', draft.document_part);
    }
    if (draft.verification_status) {
      formData.append('verification_status', draft.verification_status);
    }
    if (draft.document_expires_at) {
      const expiresAtMs = parseDateInputToUtcMs(draft.document_expires_at);
      if (expiresAtMs) {
        formData.append('document_expires_at', String(expiresAtMs));
      }
    }

    const uploadUrl = target.source === 'vehicle'
      ? `${apiFilesHost}/drivers/${encodeURIComponent(driverUploadAuth0Id)}/vehicles/${encodeURIComponent(nextVehicleId)}?doc_type=${encodeURIComponent(target.type)}`
      : `${apiFilesHost}/drivers/${encodeURIComponent(driverUploadAuth0Id)}?doc_type=${encodeURIComponent(target.type)}`;

    try {
      setUploadingKeys(currentKeys => ({ ...currentKeys, [target.key]: true }));
      setUploadMessages(currentMessages => ({ ...currentMessages, [target.key]: 'Uploading...' }));
      setError('');

      const uploadResponse = await axios({
        method: 'post',
        url: uploadUrl,
        headers: {
          Authorization: 'Bearer ' + localStorage.getItem('access_token'),
        },
        data: formData,
      });

      const uploadResult = {
        ...(uploadResponse && uploadResponse.data ? uploadResponse.data : {}),
        document_part: draft.document_part || 'full',
        verification_status: draft.verification_status || '',
      };

      const nextOptimisticUploads = {
        ...optimisticUploads,
        [optimisticTarget.key]: {
          target: optimisticTarget,
          uploadResult,
          fallbackFile: draft.file || null,
        },
      };

      setOptimisticUploads(nextOptimisticUploads);
      cacheOptimisticUploads(lookupIdentifier, nextOptimisticUploads);

      const optimisticPayload = applyOptimisticEntriesToPayload(payload || effectivePayload, nextOptimisticUploads);
      if (optimisticPayload) {
        syncPayload(optimisticPayload, true, nextOptimisticUploads);
      }

      try {
        const nextPayload = await fetchCompliancePayload(lookupIdentifier);
        syncPayload(nextPayload, true, nextOptimisticUploads);
      } catch {
        if (optimisticPayload) {
          syncPayload(optimisticPayload, true, nextOptimisticUploads);
        }
      }

      setUploadDrafts(currentDrafts => ({
        ...currentDrafts,
        [target.key]: {
          document_part: draft.document_part || 'full',
          verification_status: draft.verification_status || '',
          document_expires_at: '',
        },
      }));
      setUploadMessages(currentMessages => ({ ...currentMessages, [target.key]: 'Upload successful.' }));
    } catch (err) {
      const uploadErrorMessage = err.response && err.response.data
        ? (err.response.data.debug || err.response.data.message)
        : '';
      setUploadMessages(currentMessages => ({
        ...currentMessages,
        [target.key]: uploadErrorMessage || 'Upload failed.',
      }));
    } finally {
      setUploadingKeys(currentKeys => ({ ...currentKeys, [target.key]: false }));
    }
  };

  const handleAssetChange = (index, field, value) => {
    setAssetsDraft(currentAssets => currentAssets.map((asset, assetIndex) => {
      if (assetIndex !== index) {
        return asset;
      }

      return {
        ...asset,
        [field]: value
      };
    }));
  };

  const handleAddAsset = () => {
    setAssetsDraft(currentAssets => currentAssets.concat({
      ...createEmptyAsset(selectedVehicleId),
      asset_type: newAssetType,
    }));
  };

  const handleRemoveAsset = index => {
    setAssetsDraft(currentAssets => currentAssets.filter((_, assetIndex) => assetIndex !== index));
  };

  const handleSaveProfile = async ({ assetsOnly = false } = {}) => {
    try {
      setSaving(true);
      setError('');
      setSaveMessage('');

      const filteredAssets = assetsDraft
        .map(asset => ({
          vehicle_id: asset.vehicle_id || selectedVehicleId || null,
          asset_type: asset.asset_type,
          length_feet: asset.length_feet === '' ? null : Number(asset.length_feet),
          description: asset.description ? asset.description.trim() : null
        }))
        .filter(asset => asset.asset_type);

      const requestBody = {
        active_vehicle_id: selectedVehicleId || null,
        assets: filteredAssets,
      };

      if (!assetsOnly) {
        requestBody.onboarding_tier = onboardingDraft.onboarding_tier;
        requestBody.required_credentials = onboardingDraft.required_credentials;
        requestBody.require_profile_photo = onboardingDraft.require_profile_photo;
        requestBody.require_twic_photo = onboardingDraft.require_twic_photo;
        requestBody.require_hazmat_photo = onboardingDraft.require_hazmat_photo;
        requestBody.driver_visibility_enabled = onboardingDraft.driver_visibility_enabled;
        requestBody.completed_onboarding = onboardingDraft.completed_onboarding;
      }

      if (vehicleClassification && selectedVehicleId) {
        requestBody.vehicle_id = selectedVehicleId;
        requestBody.vehicle_classification = vehicleClassification;
      }

      const response = await requestDriverAdmin({
        driverIdentifier: lookupIdentifier,
        suffix: '/compliance-profile',
        method: 'put',
        data: requestBody
      });
      syncPayload(response.data.payload, false);
      setSaveMessage(assetsOnly ? 'Driver assets updated.' : 'Driver onboarding profile updated.');
    } catch (err) {
      const errorMessage = err.response && err.response.data
        ? (err.response.data.debug || err.response.data.message)
        : '';
      setError(errorMessage || (assetsOnly ? 'Unable to update driver assets' : 'Unable to update driver onboarding profile'));
    } finally {
      setSaving(false);
    }
  };

  const handleSendReminder = async () => {
    try {
      setReminderSending(true);
      setError('');
      setSaveMessage('');

      const phoneNumber = normalizePhoneNumber(effectivePayload && effectivePayload.driver && effectivePayload.driver.user ? effectivePayload.driver.user.phone : '');
      if (!phoneNumber) {
        throw new Error('Driver phone number is missing.');
      }

      const driverName = [
        effectivePayload && effectivePayload.driver && effectivePayload.driver.user ? effectivePayload.driver.user.firstname : '',
        effectivePayload && effectivePayload.driver && effectivePayload.driver.user ? effectivePayload.driver.user.lastname : '',
      ].filter(Boolean).join(' ');

      const reminderItems = getReminderDocumentItems([
        requiredDocumentCards,
        credentialDocumentCards,
        onboardingRequiredDocuments,
        onboardingCredentialCards,
        onboardingPhotoCards,
      ]);

      await axios({
        method: 'post',
        url: userUrls.usersHost + '/driver/send-message',
        headers: getAuthHeaders(),
        data: {
          recipient: phoneNumber,
          message: buildReminderMessage({ driverName, documents: reminderItems }),
        }
      });

      setSaveMessage(`Reminder text sent to ${phoneNumber}.`);
    } catch (err) {
      setError(err.response && err.response.data && err.response.data.message ? err.response.data.message : err.message || 'Unable to send onboarding reminder');
    } finally {
      setReminderSending(false);
    }
  };

  const handleQuickDocumentSelection = async (target, file) => {
    if (!file) {
      return;
    }

    await handleUploadDocument(target, {
      ...buildQuickUploadDefaults(target.type),
      file,
    });
  };

  const handleToggleLoadBoard = async nextEnabled => {
    try {
      setLoadBoardSaving(true);
      setError('');
      setSaveMessage('');

      const response = await requestDriverAdmin({
        driverIdentifier: lookupIdentifier,
        suffix: '/compliance-profile',
        method: 'put',
        data: {
          driver_enabled: nextEnabled,
          banned_by_branch: !nextEnabled,
          driver_status: nextEnabled ? 'active' : 'inactive'
        }
      });

      syncPayload(response.data.payload, true);
      setSaveMessage(nextEnabled ? 'Driver manually turned on.' : 'Driver manually turned off.');
    } catch (err) {
      setError(err.response && err.response.data && err.response.data.message ? err.response.data.message : 'Unable to update driver load board access');
    } finally {
      setLoadBoardSaving(false);
    }
  };

  const handleReviewDocument = async (document, decision) => {
    const reviewKey = buildDocumentReviewKey(document);
    const reviewDraft = reviewDrafts[reviewKey] || {};
    const reviewNote = reviewDraft.review_note ? reviewDraft.review_note.trim() : '';
    const documentExpiresAt = reviewDraft.document_expires_at ? parseDateInputToUtcMs(reviewDraft.document_expires_at) : null;
    
    // If no manual entry, try to use OCR value as fallback
    let finalExpiresAt = documentExpiresAt;
    if (!finalExpiresAt && documentApprovalNeedsExpiration(document)) {
      const ocrExpiration = document.ocr_text && document.ocr_text.meta
        ? document.ocr_text.meta.document_expires_at
        : null;
      if (ocrExpiration) {
        finalExpiresAt = parseExpirationValueToUtcMs(ocrExpiration);
      }

      if (!finalExpiresAt) {
        finalExpiresAt = parseExpirationValueToUtcMs(getDocumentExpirationValue(document));
      }
    }

    if (decision === 'approve' && documentApprovalNeedsExpiration(document) && !finalExpiresAt) {
      setReviewMessages(currentMessages => ({
        ...currentMessages,
        [reviewKey]: 'Enter an expiration date before approving this document.',
      }));
      return;
    }

    if (decision === 'deny' && !reviewNote) {
      setReviewMessages(currentMessages => ({
        ...currentMessages,
        [reviewKey]: 'Enter a denial reason before sending the update request to the driver.',
      }));
      return;
    }

    try {
      setReviewingKeys(currentKeys => ({ ...currentKeys, [reviewKey]: true }));
      setReviewMessages(currentMessages => ({ ...currentMessages, [reviewKey]: 'Saving review...' }));
      setError('');
      setSaveMessage('');

      const response = await requestDriverAdmin({
        driverIdentifier: lookupIdentifier,
        suffix: '/document-review',
        method: 'put',
        data: {
          source: document.source || 'user',
          doc_type: document.doc_type || document.type,
          file_name: document.file_name,
          vehicle_id: document.vehicle_id || '',
          decision,
          review_note: reviewNote || undefined,
          document_expires_at: finalExpiresAt || undefined,
        },
      });

      syncPayload(response.data.payload, true);

      let nextMessage = decision === 'approve'
        ? `${document.label} approved.`
        : `${document.label} denied.`;

      if (decision === 'deny') {
        const phoneNumber = normalizePhoneNumber(payload && payload.driver && payload.driver.user ? payload.driver.user.phone : '');
        const driverName = [
          payload && payload.driver && payload.driver.user ? payload.driver.user.firstname : '',
          payload && payload.driver && payload.driver.user ? payload.driver.user.lastname : '',
        ].filter(Boolean).join(' ');

        if (phoneNumber) {
          try {
            await axios({
              method: 'post',
              url: userUrls.usersHost + '/driver/send-message',
              headers: getAuthHeaders(),
              data: {
                recipient: phoneNumber,
                message: buildDeniedDocumentMessage({
                  driverName,
                  documentLabel: document.label,
                  reviewNote,
                }),
              },
            });
            nextMessage += ' Driver message sent.';
          } catch (messageError) {
            nextMessage += ' Review saved, but the driver message failed to send.';
          }
        } else {
          nextMessage += ' Review saved, but the driver has no phone number on file.';
        }
      }

      setReviewDrafts(currentDrafts => ({
        ...currentDrafts,
        [reviewKey]: {
          document_expires_at: '',
          review_note: '',
        },
      }));
      setReviewMessages(currentMessages => ({ ...currentMessages, [reviewKey]: nextMessage }));
    } catch (err) {
      const reviewErrorMessage = err.response && err.response.data
        ? (err.response.data.debug || err.response.data.message)
        : '';
      setReviewMessages(currentMessages => ({
        ...currentMessages,
        [reviewKey]: reviewErrorMessage || 'Unable to save document review.',
      }));
    } finally {
      setReviewingKeys(currentKeys => ({ ...currentKeys, [reviewKey]: false }));
    }
  };

  const handleUpdateDocumentExpiration = async document => {
    const reviewKey = buildDocumentReviewKey(document);
    const reviewDraft = reviewDrafts[reviewKey] || {};
    const documentExpiresAt = reviewDraft.document_expires_at ? parseDateInputToUtcMs(reviewDraft.document_expires_at) : null;

    if (!documentExpiresAt) {
      setReviewMessages(currentMessages => ({
        ...currentMessages,
        [reviewKey]: 'Enter the corrected expiration date before saving.',
      }));
      return;
    }

    try {
      setReviewingKeys(currentKeys => ({ ...currentKeys, [reviewKey]: true }));
      setReviewMessages(currentMessages => ({ ...currentMessages, [reviewKey]: 'Saving expiration...' }));
      setError('');
      setSaveMessage('');

      const response = await requestDriverAdmin({
        driverIdentifier: lookupIdentifier,
        suffix: '/document-review',
        method: 'put',
        data: {
          source: document.source || 'user',
          doc_type: document.doc_type || document.type,
          file_name: document.file_name,
          vehicle_id: document.vehicle_id || '',
          decision: 'approve',
          document_expires_at: documentExpiresAt,
        },
      });

      syncPayload(response.data.payload, true);
      setReviewDrafts(currentDrafts => ({
        ...Object.keys(currentDrafts).reduce((nextDrafts, key) => {
          if (key !== reviewKey) {
            nextDrafts[key] = currentDrafts[key];
          }
          return nextDrafts;
        }, {}),
      }));
      setReviewMessages(currentMessages => ({
        ...currentMessages,
        [reviewKey]: `${document.label} expiration updated.`,
      }));
    } catch (err) {
      const updateErrorMessage = err.response && err.response.data
        ? (err.response.data.debug || err.response.data.message)
        : '';
      setReviewMessages(currentMessages => ({
        ...currentMessages,
        [reviewKey]: updateErrorMessage || 'Unable to update expiration date.',
      }));
    } finally {
      setReviewingKeys(currentKeys => ({ ...currentKeys, [reviewKey]: false }));
    }
  };

  if (loading) {
    return <div style={{ ...panelStyles.container, ...panelStyles.loading }}>Loading compliance summary...</div>;
  }

  const fallbackPayload = !payload && isDispatchVariant ? {
    driver: {
      auth0_id: driverAuth0Id || lookupIdentifier || 'Unavailable',
      branch_id: dispatchContext && dispatchContext.branch_id ? dispatchContext.branch_id : null,
      banned_by_branch: false,
      load_board_disabled: false,
      disable_reasons: [],
    },
    vehicles: [],
    assets: [],
    compliance: {
      compliance_status: 'restricted',
      primary_vehicle: dispatchContext && dispatchContext.vehicle ? dispatchContext.vehicle : null,
      required_documents: [],
      credential_documents: [],
      valid_credentials: [],
      blocking_reasons: error ? [error] : [],
      warnings: [],
    },
  } : null;

  const effectivePayload = payload || fallbackPayload;

  if (!effectivePayload || !effectivePayload.compliance) {
    return error ? <div style={{ ...panelStyles.container, ...panelStyles.error }}>{error}</div> : null;
  }

  const { compliance, assets } = effectivePayload;
  const badgeColors = getStatusColors(compliance.compliance_status);
  const validCredentials = compliance.valid_credentials && compliance.valid_credentials.length > 0
    ? compliance.valid_credentials.join(', ')
    : 'None';
  const primaryVehicle = compliance.primary_vehicle || vehicles[0] || null;
  const displayVehicle = selectedVehicle || primaryVehicle || null;
  const branchId = effectivePayload.driver && effectivePayload.driver.branch_id ? effectivePayload.driver.branch_id : 'Not assigned';
  const manualDisabled = Boolean(effectivePayload.driver && effectivePayload.driver.banned_by_branch);
  const loadBoardDisabled = Boolean(effectivePayload.driver && effectivePayload.driver.load_board_disabled);
  const disableReasons = effectivePayload.driver && effectivePayload.driver.disable_reasons ? effectivePayload.driver.disable_reasons : [];
  const onboardingStatusColors = getStatusColors(onboarding && onboarding.status ? onboarding.status : 'action_required');
  const quickRequiredUploadTargets = requiredDocumentCards.map(document => ({
    key: buildDocumentTargetKey({
      type: document.type,
      source: document.type === 'proof_of_insurance' || document.type === 'vehicle_registration' ? 'vehicle' : 'user',
      vehicle_id: document.vehicle_id || selectedVehicleId || '',
    }, selectedVehicleId),
    type: document.type,
    label: document.label,
    source: document.type === 'proof_of_insurance' || document.type === 'vehicle_registration' ? 'vehicle' : 'user',
    vehicle_id: document.vehicle_id || selectedVehicleId || '',
  }));
  const selectedCredentialDefinition = CREDENTIAL_UPLOAD_OPTIONS.find(option => option.type === selectedCredentialType) || CREDENTIAL_UPLOAD_OPTIONS[0];
  const featuredRequiredDocument = requiredDocumentCards.find(document => document.type === 'drivers_license') || requiredDocumentCards[0] || null;
  const secondaryRequiredDocuments = requiredDocumentCards.filter(document => !featuredRequiredDocument || document.type !== featuredRequiredDocument.type);
  const orderedSecondaryRequiredDocuments = sortRequiredDocuments(secondaryRequiredDocuments);
  const selectedCredentialTarget = selectedCredentialDefinition ? {
    key: buildDocumentTargetKey({ type: selectedCredentialDefinition.type, source: 'user' }, selectedVehicleId),
    type: selectedCredentialDefinition.type,
    label: selectedCredentialDefinition.label,
    source: 'user',
  } : null;

  const renderVehicleInfoCard = () => (
    <div style={panelStyles.vehicleInfoCard}>
      {availableVehicles.length > 1 ? (
        <div>
          <label style={panelStyles.label}>Selected Vehicle</label>
          <Input type="select" value={selectedVehicleId} onChange={event => setActiveVehicleId(event.target.value)} disabled={saving || availableVehicles.length === 0}>
            {availableVehicles.map(vehicle => (
              <option key={vehicle.vehicle_id} value={vehicle.vehicle_id}>{getVehicleOptionLabel(vehicle)}</option>
            ))}
          </Input>
          <p style={{ ...panelStyles.vehicleSelectorCopy, marginTop: '6px' }}>
            Switching the vehicle updates Insurance and Registration for that vehicle.
          </p>
        </div>
      ) : null}
      <div><strong>Vehicle:</strong> {getVehicleSummary(displayVehicle)}</div>
      <div><strong>Vehicle Type:</strong> {displayVehicle && (displayVehicle.vehicle_classification || displayVehicle.type) ? getVehicleTypeLabel(displayVehicle.vehicle_classification || displayVehicle.type) : 'N/A'}</div>
      <div><strong>Plate:</strong> {displayVehicle && displayVehicle.plate_value ? displayVehicle.plate_value : 'N/A'}</div>
      <div><strong>Credentials:</strong> {validCredentials}</div>
    </div>
  );

  const getDocumentReviewProps = document => {
    if (!documentNeedsManualReview(document)) {
      return {};
    }

    const reviewKey = buildDocumentReviewKey(document);
    const existingDraft = reviewDrafts[reviewKey] || {};
    const draft = buildReviewDraft(document, existingDraft);
    
    return {
      reviewState: {
        draft,
        busy: Boolean(reviewingKeys[reviewKey]),
        message: reviewMessages[reviewKey] || '',
      },
      onReviewDraftChange: patch => handleReviewDraftChange(reviewKey, patch),
      onReviewAction: decision => handleReviewDocument(document, decision),
    };
  };

  const getDocumentUploadProps = document => {
    const target = uploadTargetsByKey[buildDocumentTargetKey(document, selectedVehicleId)] || null;

    if (!target) {
      return {};
    }

    return {
      uploadBusy: Boolean(uploadingKeys[target.key]),
      uploadMessage: uploadMessages[target.key] || 'Click the photo to open it or upload to replace it.',
      uploadInputId: `document-upload-${buildDocumentReviewKey(document).replace(/[^a-zA-Z0-9_-]/g, '-')}`,
      uploadAction: file => handleQuickDocumentSelection(target, file),
    };
  };

  const getDocumentExpiryEditProps = document => {
    if (!shouldShowExpiryEditor(document)) {
      return {};
    }

    const reviewKey = buildDocumentReviewKey(document);
    const existingDraft = reviewDrafts[reviewKey] || {};

    return {
      expiryEditState: {
        draft: buildExpiryEditDraft(document, existingDraft),
        busy: Boolean(reviewingKeys[reviewKey]),
        message: reviewMessages[reviewKey] || '',
      },
      onExpiryDraftChange: patch => handleReviewDraftChange(reviewKey, patch),
      onExpirySave: () => handleUpdateDocumentExpiration(document),
    };
  };

  const renderProfileEditor = compact => (
    <div style={compact ? panelStyles.dispatchEditorPanel : panelStyles.editor}>
      <h5 style={panelStyles.sectionTitle}>Driver Profile</h5>
      <div style={panelStyles.editorGrid}>
        <div>
          <label style={panelStyles.label}>Active Vehicle</label>
          <Input type="select" value={selectedVehicleId} onChange={event => setActiveVehicleId(event.target.value)} disabled={saving || availableVehicles.length === 0}>
            {availableVehicles.length === 0 ? <option value="">No vehicles on file</option> : null}
            {availableVehicles.map(vehicle => (
              <option key={vehicle.vehicle_id} value={vehicle.vehicle_id}>
                {(vehicle.make || 'Vehicle') + (vehicle.model ? ` ${vehicle.model}` : '') + (vehicle.plate_value ? ` (${vehicle.plate_value})` : '')}
              </option>
            ))}
          </Input>
        </div>
        <div>
          <label style={panelStyles.label}>Vehicle Classification</label>
          <Input type="select" value={vehicleClassification} onChange={event => setVehicleClassification(event.target.value)} disabled={saving || vehicles.length === 0}>
            <option value="">Select classification</option>
            {VEHICLE_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </Input>
        </div>
      </div>

      <h5 style={panelStyles.sectionTitle}>Onboarding Settings</h5>
      <div style={panelStyles.editorGrid}>
        <div>
          <label style={panelStyles.label}>Onboarding Tier</label>
          <Input type="select" value={onboardingDraft.onboarding_tier} onChange={event => handleOnboardingFieldChange('onboarding_tier', event.target.value)} disabled={saving}>
            {ONBOARDING_TIER_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </Input>
        </div>
        <div>
          <label style={panelStyles.label}>Onboarding Completion</label>
          <div style={panelStyles.checkboxCard}>
            <label style={panelStyles.checkboxRow}>
              <Input type="checkbox" checked={onboardingDraft.completed_onboarding} onChange={event => handleOnboardingFieldChange('completed_onboarding', event.target.checked)} disabled={saving} />
              <span>Mark onboarding complete</span>
            </label>
            <label style={panelStyles.checkboxRow}>
              <Input type="checkbox" checked={onboardingDraft.driver_visibility_enabled} onChange={event => handleOnboardingFieldChange('driver_visibility_enabled', event.target.checked)} disabled={saving} />
              <span>Show profile photo to customer</span>
            </label>
          </div>
        </div>
      </div>

      <div style={panelStyles.checkboxGrid}>
        <div style={panelStyles.checkboxCard}>
          <strong style={{ fontSize: '0.88rem' }}>Required Photo Rules</strong>
          <label style={panelStyles.checkboxRow}>
            <Input type="checkbox" checked={onboardingDraft.require_profile_photo} onChange={event => handleOnboardingFieldChange('require_profile_photo', event.target.checked)} disabled={saving} />
            <span>Require profile photo</span>
          </label>
          <label style={panelStyles.checkboxRow}>
            <Input type="checkbox" checked={onboardingDraft.require_twic_photo} onChange={event => handleOnboardingFieldChange('require_twic_photo', event.target.checked)} disabled={saving} />
            <span>Require TWIC photo upload</span>
          </label>
          <label style={panelStyles.checkboxRow}>
            <Input type="checkbox" checked={onboardingDraft.require_hazmat_photo} onChange={event => handleOnboardingFieldChange('require_hazmat_photo', event.target.checked)} disabled={saving} />
            <span>Require Hazmat photo upload</span>
          </label>
        </div>

        <div style={panelStyles.checkboxCard}>
          <strong style={{ fontSize: '0.88rem' }}>Required Credentials</strong>
          {CREDENTIAL_DOCUMENT_DEFINITIONS.map(option => (
            <label key={option.value || option.type} style={panelStyles.checkboxRow}>
              <Input type="checkbox" checked={onboardingDraft.required_credentials.includes(option.type)} onChange={() => handleToggleCredentialRequirement(option.type)} disabled={saving} />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      <h5 style={panelStyles.sectionTitle}>Upload Onboarding Documents</h5>
      <div style={panelStyles.uploadGrid}>
        {uploadTargets.map(target => {
          const draft = uploadDrafts[target.key] || {
            document_part: DOC_TYPES_WITH_PARTS.has(target.type) ? 'full' : '',
            verification_status: '',
            document_expires_at: '',
            vehicle_id: target.vehicle_id || '',
          };
          const uploadMessage = uploadMessages[target.key] || '';
          const isUploading = Boolean(uploadingKeys[target.key]);

          return (
            <div key={target.key} style={panelStyles.uploadRow}>
              <div style={panelStyles.uploadHeader}>
                <div>
                  <strong>{target.label}</strong>
                  <div style={panelStyles.uploadHint}>
                    {target.source === 'vehicle' ? 'Uploads to the currently selected vehicle record.' : 'Uploads to the driver document record.'}
                  </div>
                </div>
                <ValidStatusPill status={((requiredDocumentCards.concat(onboardingRequiredDocuments).concat(onboardingCredentialCards).concat(onboardingPhotoCards)).find(document => document.type === target.type) || {}).status || 'missing'} />
              </div>

              <div style={panelStyles.uploadMetaGrid}>
                <div>
                  <label style={panelStyles.label}>File</label>
                  <Input type="file" accept="application/pdf,image/*" onChange={event => handleUploadDraftChange(target.key, { file: event.target.files && event.target.files[0] ? event.target.files[0] : null })} disabled={isUploading} />
                </div>
                {target.source === 'vehicle' ? (
                  <div>
                    <label style={panelStyles.label}>Vehicle</label>
                    <Input type="select" value={draft.vehicle_id || target.vehicle_id || ''} onChange={event => handleUploadDraftChange(target.key, { vehicle_id: event.target.value })} disabled={isUploading || vehicles.length === 0}>
                      <option value="">Select vehicle</option>
                      {vehicles.map(vehicle => (
                        <option key={vehicle.vehicle_id} value={vehicle.vehicle_id}>
                          {(vehicle.make || 'Vehicle') + (vehicle.model ? ` ${vehicle.model}` : '') + (vehicle.plate_value ? ` (${vehicle.plate_value})` : '')}
                        </option>
                      ))}
                    </Input>
                  </div>
                ) : null}
                {DOC_TYPES_WITH_PARTS.has(target.type) ? (
                  <div>
                    <label style={panelStyles.label}>Document Part</label>
                    <Input type="select" value={draft.document_part || 'full'} onChange={event => handleUploadDraftChange(target.key, { document_part: event.target.value })} disabled={isUploading}>
                      {DOCUMENT_PART_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </Input>
                  </div>
                ) : null}
                <div>
                  <label style={panelStyles.label}>Verification</label>
                  <Input type="select" value={draft.verification_status || ''} onChange={event => handleUploadDraftChange(target.key, { verification_status: event.target.value })} disabled={isUploading}>
                    {VERIFICATION_STATUS_OPTIONS.map(option => (
                      <option key={option.value || 'default'} value={option.value}>{option.label}</option>
                    ))}
                  </Input>
                </div>
                <div>
                  <label style={panelStyles.label}>Expiry Override</label>
                  <Input type="date" value={draft.document_expires_at || ''} onChange={event => handleUploadDraftChange(target.key, { document_expires_at: event.target.value })} disabled={isUploading} />
                </div>
              </div>

              <div style={panelStyles.actionsRow}>
                <Button className="btn btn-default" onClick={() => handleUploadDocument(target)} disabled={isUploading}>
                  {isUploading ? 'Uploading...' : 'Upload / Replace'}
                </Button>
                {uploadMessage ? <span style={uploadMessage === 'Upload successful.' ? panelStyles.successCopy : panelStyles.copy}>{uploadMessage}</span> : null}
              </div>
            </div>
          );
        })}
      </div>

      <div style={panelStyles.assetsHeader}>
        <h5 style={{ marginBottom: 0 }}>Trailer Assets</h5>
        <div style={panelStyles.quickPickerRow}>
          <Input type="select" value={newAssetType} onChange={event => setNewAssetType(event.target.value)} disabled={saving} style={panelStyles.quickPickerSelect}>
            {ASSET_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </Input>
          <Button className="btn btn-default btn-sm" onClick={handleAddAsset} disabled={saving}>
            Add New
          </Button>
        </div>
      </div>

      {assetsDraft.length > 0 ? assetsDraft.map((asset, index) => (
        <div key={asset.asset_id || `asset-${index}`} style={panelStyles.assetRow}>
          <div>
            <label style={panelStyles.label}>Asset Type</label>
            <Input type="select" value={asset.asset_type} onChange={event => handleAssetChange(index, 'asset_type', event.target.value)} disabled={saving}>
              {ASSET_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </Input>
          </div>
          <div>
            <label style={panelStyles.label}>Length</label>
            <Input type="number" min="0" value={asset.length_feet} onChange={event => handleAssetChange(index, 'length_feet', event.target.value)} disabled={saving} placeholder="Feet" />
          </div>
          <div>
            <label style={panelStyles.label}>Linked Vehicle</label>
            <Input type="select" value={asset.vehicle_id || ''} onChange={event => handleAssetChange(index, 'vehicle_id', event.target.value)} disabled={saving || vehicles.length === 0}>
              <option value="">Unlinked</option>
              {vehicles.map(vehicle => (
                <option key={vehicle.vehicle_id} value={vehicle.vehicle_id}>
                  {(vehicle.make || 'Vehicle') + (vehicle.model ? ` ${vehicle.model}` : '')}
                </option>
              ))}
            </Input>
          </div>
          <div>
            <label style={panelStyles.label}>Description</label>
            <Input type="text" value={asset.description} onChange={event => handleAssetChange(index, 'description', event.target.value)} disabled={saving} placeholder="Optional notes" />
          </div>
          <div style={panelStyles.assetRemove}>
            <Button className="btn btn-link btn-sm" onClick={() => handleRemoveAsset(index)} disabled={saving}>
              Remove
            </Button>
          </div>
        </div>
      )) : (
        <p style={panelStyles.copy}>No trailer assets registered.</p>
      )}

      <div style={panelStyles.actionsRow}>
        <Button className="btn btn-success" onClick={handleSaveProfile} disabled={saving}>
          {saving ? 'Saving...' : 'Save Driver Profile'}
        </Button>
        {!compact ? (
          <Button className="btn btn-default" onClick={handleSendReminder} disabled={saving || reminderSending}>
            {reminderSending ? 'Sending...' : 'Send Reminder'}
          </Button>
        ) : null}
        {saveMessage ? <span style={panelStyles.successCopy}>{saveMessage}</span> : null}
      </div>
    </div>
  );

  return (
    <div style={panelStyles.container}>
      <div style={panelStyles.headerRow}>
        <div>
          <h4 style={panelStyles.heading}>{isDispatchVariant ? 'Driver Credentials' : 'Driver Compliance'}</h4>
          {!isDispatchVariant ? (
            <p style={panelStyles.copy}>Shared compliance summary for vehicles, trailers, credentials, and required documents.</p>
          ) : null}
        </div>
        <div style={{ ...panelStyles.statusBadgeBase, background: badgeColors.background, color: badgeColors.color, border: `1px solid ${badgeColors.border}` }}>
          <span>{compliance.compliance_status === 'eligible' ? '✓' : '✕'}</span>
          <span>{compliance.compliance_status === 'eligible' ? 'Valid' : 'Needs Attention'}</span>
        </div>
      </div>

      {isDispatchVariant ? (
        <>
          <div style={panelStyles.dispatchMetaStrip}>
            <div style={panelStyles.dispatchMetaCard}>
              <h5 style={panelStyles.sectionTitle}>Load Board Status</h5>
              <div style={{ wordBreak: 'break-all' }}><strong>Branch ID:</strong> {branchId}</div>
              <div>
                <strong>Load Board:</strong> {loadBoardDisabled ? 'Off' : 'On'}
              </div>
              {disableReasons.length > 0 ? disableReasons.map(reason => (
                <div key={reason} style={panelStyles.disableReason}>{reason}</div>
              )) : (
                <div style={panelStyles.copy}>Auto-off when license, insurance, or registration is expired or missing.</div>
              )}
              <div>
                <Button className={manualDisabled ? 'btn btn-success btn-sm' : 'btn btn-danger btn-sm'} style={panelStyles.dispatchActionButton} onClick={() => handleToggleLoadBoard(manualDisabled)} disabled={loadBoardSaving || !payload}>
                  {loadBoardSaving ? 'Saving...' : manualDisabled ? 'Turn Driver On' : 'Turn Driver Off'}
                </Button>
              </div>
            </div>
            <div style={panelStyles.dispatchMetaCard}>
              <h5 style={panelStyles.sectionTitle}>Vehicle Information</h5>
              {renderVehicleInfoCard()}
            </div>
            {headerAside ? (
              <div style={panelStyles.dispatchAsideCard}>
                {headerAside}
              </div>
            ) : null}
          </div>

          <div style={panelStyles.dispatchGrid}>
            <div style={panelStyles.dispatchColumn}>
              <h5 style={panelStyles.sectionTitle}>Required Documents</h5>
              <div style={panelStyles.requiredDocumentsLayout}>
                {featuredRequiredDocument ? (() => {
                  const target = quickRequiredUploadTargets.find(item => item.type === featuredRequiredDocument.type);
                  const targetKey = target ? target.key : featuredRequiredDocument.type;

                  return (
                    <DocumentCard
                      key={featuredRequiredDocument.type}
                      document={featuredRequiredDocument}
                      driverAuth0Id={resolvedDriverAuth0Id}
                      driverIdentifier={lookupIdentifier}
                      compact
                      layout="featured"
                      uploadBusy={Boolean(uploadingKeys[targetKey])}
                      uploadMessage={uploadMessages[targetKey] || 'Click the photo to open it or upload to replace it.'}
                      uploadInputId={`required-doc-upload-${featuredRequiredDocument.type}`}
                      uploadAction={file => handleQuickDocumentSelection(target, file)}
                      {...getDocumentExpiryEditProps(featuredRequiredDocument)}
                      {...getDocumentReviewProps(featuredRequiredDocument)}
                    />
                  );
                })() : null}
                <div style={panelStyles.requiredDocumentsSecondaryGrid}>
                {orderedSecondaryRequiredDocuments.map(document => {
                  const target = quickRequiredUploadTargets.find(item => item.type === document.type);
                  const targetKey = target ? target.key : document.type;

                  return (
                    <DocumentCard
                      key={document.type}
                      document={document}
                      driverAuth0Id={resolvedDriverAuth0Id}
                      driverIdentifier={lookupIdentifier}
                      layout="required-compact"
                      uploadBusy={Boolean(uploadingKeys[targetKey])}
                      uploadMessage={uploadMessages[targetKey] || 'Click the photo to open it or upload to replace it.'}
                      uploadInputId={`required-doc-upload-${document.type}`}
                      uploadAction={file => handleQuickDocumentSelection(target, file)}
                      {...getDocumentExpiryEditProps(document)}
                      {...getDocumentReviewProps(document)}
                    />
                  );
                })}
                </div>
              </div>
            </div>

            <div style={panelStyles.dispatchColumn}>
              <div style={panelStyles.dispatchStack}>
                <div>
                  <h5 style={panelStyles.sectionTitle}>Assets</h5>
                  <div style={panelStyles.quickPickerRow}>
                    <Input type="select" value={newAssetType} onChange={event => setNewAssetType(event.target.value)} disabled={saving} style={panelStyles.quickPickerSelect}>
                      {ASSET_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </Input>
                    <Button className="btn btn-default btn-sm" onClick={handleAddAsset} disabled={saving}>
                      Add New
                    </Button>
                    <Button className="btn btn-success btn-sm" onClick={() => handleSaveProfile({ assetsOnly: true })} disabled={saving}>
                      {saving ? 'Saving...' : 'Save Assets'}
                    </Button>
                  </div>
                  {assetsDraft.length > 0 ? (
                    <div>
                      {assetsDraft.map((asset, index) => (
                        <div key={asset.asset_id || `asset-${index}`} style={panelStyles.assetRow}>
                          <div>
                            <label style={panelStyles.label}>Asset Type</label>
                            <Input type="select" value={asset.asset_type} onChange={event => handleAssetChange(index, 'asset_type', event.target.value)} disabled={saving}>
                              {ASSET_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </Input>
                          </div>
                          <div>
                            <label style={panelStyles.label}>Length</label>
                            <Input type="number" min="0" value={asset.length_feet} onChange={event => handleAssetChange(index, 'length_feet', event.target.value)} disabled={saving} placeholder="Feet" />
                          </div>
                          <div>
                            <label style={panelStyles.label}>Linked Vehicle</label>
                            <Input type="select" value={asset.vehicle_id || selectedVehicleId || ''} onChange={event => handleAssetChange(index, 'vehicle_id', event.target.value)} disabled={saving || availableVehicles.length === 0}>
                              <option value="">Unlinked</option>
                              {availableVehicles.map(vehicle => (
                                <option key={vehicle.vehicle_id} value={vehicle.vehicle_id}>
                                  {(vehicle.make || 'Vehicle') + (vehicle.model ? ` ${vehicle.model}` : '')}
                                </option>
                              ))}
                            </Input>
                          </div>
                          <div>
                            <label style={panelStyles.label}>Description</label>
                            <Input type="text" value={asset.description} onChange={event => handleAssetChange(index, 'description', event.target.value)} disabled={saving} placeholder="Optional notes" />
                          </div>
                          <div style={panelStyles.assetRemove}>
                            <Button className="btn btn-link btn-sm" onClick={() => handleRemoveAsset(index)} disabled={saving}>
                              Remove
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={panelStyles.emptyStateCard}>No trailer assets registered.</div>
                  )}
                </div>

                <div>
                  <h5 style={panelStyles.sectionTitle}>Expired / Expiring Documents</h5>
                  {onboardingExpiringCards.length > 0 ? (
                    <div style={panelStyles.compactDocumentGrid}>
                      {onboardingExpiringCards.map(document => (
                        <DocumentCard
                          key={`dispatch-expiring-${document.type}-${document.vehicle_id || 'user'}-${document.file_name || 'current'}`}
                          document={document}
                          driverAuth0Id={resolvedDriverAuth0Id}
                          driverIdentifier={lookupIdentifier}
                          compact
                          {...getDocumentUploadProps(document)}
                          {...getDocumentExpiryEditProps(document)}
                          {...getDocumentReviewProps(document)}
                        />
                      ))}
                    </div>
                  ) : (
                    <div style={panelStyles.emptyStateCard}>No expired or expiring onboarding documents.</div>
                  )}
                </div>

                <div>
                  <h5 style={panelStyles.sectionTitle}>Credentials</h5>
                  <div style={panelStyles.quickPickerRow}>
                    <Input type="select" value={selectedCredentialType} onChange={event => setSelectedCredentialType(event.target.value)} disabled={saving} style={panelStyles.quickPickerSelect}>
                      {CREDENTIAL_UPLOAD_OPTIONS.map(option => (
                        <option key={option.type} value={option.type}>{option.label}</option>
                      ))}
                    </Input>
                    {selectedCredentialTarget ? (
                      <>
                        <label htmlFor="credential-quick-upload" className="btn btn-default btn-sm" style={{ marginBottom: 0 }}>
                          Upload Credential
                        </label>
                        <input
                          id="credential-quick-upload"
                          type="file"
                          accept="application/pdf,image/*"
                          style={panelStyles.hiddenInput}
                          onChange={event => {
                            const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
                            handleQuickDocumentSelection(selectedCredentialTarget, file);
                            event.target.value = '';
                          }}
                          disabled={Boolean(uploadingKeys[selectedCredentialTarget.key])}
                        />
                      </>
                    ) : null}
                    {selectedCredentialTarget && uploadMessages[selectedCredentialTarget.key] ? (
                      <span style={panelStyles.inlineMessage}>{uploadMessages[selectedCredentialTarget.key]}</span>
                    ) : null}
                  </div>
                  {credentialDocumentCards.length > 0 ? (
                    <div style={panelStyles.compactDocumentGrid}>
                      {credentialDocumentCards.map(document => (
                        <DocumentCard key={document.type} document={document} driverAuth0Id={resolvedDriverAuth0Id} driverIdentifier={lookupIdentifier} compact {...getDocumentReviewProps(document)} />
                      ))}
                    </div>
                  ) : (
                    <div style={panelStyles.emptyStateCard}>No credentials on file</div>
                  )}
                </div>
                {compliance.blocking_reasons && compliance.blocking_reasons.length > 0 ? (
                  <div style={{ ...panelStyles.dispatchAlert, background: 'rgba(231, 76, 60, 0.16)', color: '#ffb3aa' }}>
                    {compliance.blocking_reasons.join(' | ')}
                  </div>
                ) : null}
                {compliance.warnings && compliance.warnings.length > 0 ? (
                  <div style={{ ...panelStyles.dispatchAlert, background: 'rgba(243, 156, 18, 0.16)', color: '#ffd189' }}>
                    {compliance.warnings.join(' | ')}
                  </div>
                ) : null}
                {showEditor ? (
                  <div style={panelStyles.dispatchBodySection}>
                    <div style={panelStyles.dispatchEditorHeader}>
                      <Button className="btn btn-default btn-sm" onClick={() => setDispatchEditorOpen(current => !current)}>
                        {dispatchEditorOpen ? 'Hide Profile Controls' : 'Edit Driver Profile'}
                      </Button>
                      <Button className="btn btn-default btn-sm" onClick={handleSendReminder} disabled={saving || reminderSending}>
                        {reminderSending ? 'Sending...' : 'Send Reminder'}
                      </Button>
                    </div>
                    {dispatchEditorOpen ? renderProfileEditor(true) : null}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </>
      ) : null}

      {!isDispatchVariant ? (
        <div style={panelStyles.metaGrid}>
          <div style={panelStyles.metaCard}>
            <strong>Primary Vehicle:</strong> {getVehicleSummary(compliance.primary_vehicle)}
          </div>
          <div style={panelStyles.metaCard}>
            <strong>Valid Credentials:</strong> {validCredentials}
          </div>
          <div style={panelStyles.metaCard}>
            <strong>Onboarding Tier:</strong> {onboarding && onboarding.tier_label ? onboarding.tier_label : 'Non-DOT'}
          </div>
          <div style={panelStyles.metaCard}>
            <strong>Onboarding Status:</strong>{' '}
            <span style={{ ...panelStyles.statusPillBase, background: onboardingStatusColors.background, color: onboardingStatusColors.color, border: `1px solid ${onboardingStatusColors.border}` }}>
              {formatStatus(onboarding && onboarding.status ? onboarding.status : 'action_required')}
            </span>
          </div>
        </div>
      ) : null}

      {!isDispatchVariant && error ? (
        <div style={{ ...panelStyles.alertBase, background: 'rgba(231, 76, 60, 0.16)', color: '#ffb3aa' }}>
          <div>{error}</div>
        </div>
      ) : null}

      {!isDispatchVariant && compliance.blocking_reasons && compliance.blocking_reasons.length > 0 ? (
        <div style={{ ...panelStyles.alertBase, background: 'rgba(231, 76, 60, 0.16)', color: '#ffb3aa' }}>
          {compliance.blocking_reasons.map(reason => (
            <div key={reason}>{reason}</div>
          ))}
        </div>
      ) : null}

      {!isDispatchVariant && compliance.warnings && compliance.warnings.length > 0 ? (
        <div style={{ ...panelStyles.alertBase, background: 'rgba(243, 156, 18, 0.16)', color: '#ffd189' }}>
          {compliance.warnings.map(warning => (
            <div key={warning}>{warning}</div>
          ))}
        </div>
      ) : null}

      {!isDispatchVariant && onboarding && onboarding.blocking_reasons && onboarding.blocking_reasons.length > 0 ? (
        <div style={{ ...panelStyles.alertBase, background: 'rgba(231, 76, 60, 0.16)', color: '#ffb3aa' }}>
          {onboarding.blocking_reasons.map(reason => (
            <div key={reason}>{reason}</div>
          ))}
        </div>
      ) : null}

      {!isDispatchVariant && onboarding && onboarding.warnings && onboarding.warnings.length > 0 ? (
        <div style={{ ...panelStyles.alertBase, background: 'rgba(243, 156, 18, 0.16)', color: '#ffd189' }}>
          {onboarding.warnings.map(warning => (
            <div key={warning}>{warning}</div>
          ))}
        </div>
      ) : null}

      {!isDispatchVariant ? (
        <>
          <h5 style={panelStyles.sectionTitle}>Onboarding Overview</h5>
          <div style={panelStyles.metaGrid}>
            <div style={panelStyles.metaCard}>
              <strong>Completed:</strong> {onboarding && onboarding.completed_onboarding ? 'Yes' : 'No'}
            </div>
            <div style={panelStyles.metaCard}>
              <strong>Reminder Window:</strong> {onboarding && onboarding.reminder_window_days ? `${onboarding.reminder_window_days} days` : '30 days'}
            </div>
            <div style={panelStyles.metaCard}>
              <strong>Customer Visibility:</strong> {onboarding && onboarding.driver_visibility && onboarding.driver_visibility.enabled ? 'Enabled' : 'Disabled'}
            </div>
            <div style={panelStyles.metaCard}>
              <strong>Profile Photo:</strong> {onboardingPhotoCards.length > 0 && onboardingPhotoCards[0].status === 'valid' ? 'Ready' : 'Missing'}
            </div>
          </div>

          <h5 style={panelStyles.sectionTitle}>Onboarding Documents</h5>
          <div style={panelStyles.documentGrid}>
            {onboardingRequiredDocuments.map(document => (
              <DocumentCard key={`onboarding-doc-${document.type}`} document={document} driverAuth0Id={resolvedDriverAuth0Id} driverIdentifier={lookupIdentifier} {...getDocumentReviewProps(document)} />
            ))}
          </div>

          <h5 style={panelStyles.sectionTitle}>Onboarding Credentials</h5>
          {onboardingCredentialCards.length > 0 ? (
            <div style={panelStyles.documentGrid}>
              {onboardingCredentialCards.map(document => (
                <DocumentCard key={`onboarding-credential-${document.type}`} document={document} driverAuth0Id={resolvedDriverAuth0Id} driverIdentifier={lookupIdentifier} {...getDocumentReviewProps(document)} />
              ))}
            </div>
          ) : (
            <p style={panelStyles.copy}>No onboarding credential photos are currently required.</p>
          )}

          <h5 style={panelStyles.sectionTitle}>Profile Photo And Visibility</h5>
          {onboardingPhotoCards.map(document => (
            <div key={`onboarding-photo-${document.type}`} style={panelStyles.onboardingPreview}>
              {document.signed_url ? (
                <img src={document.signed_url} alt={document.label} style={panelStyles.onboardingPreviewImage} />
              ) : (
                <div style={{ ...panelStyles.documentPreview, margin: 0, minHeight: '92px' }}>Missing</div>
              )}
              <div style={panelStyles.dispatchStack}>
                <div style={panelStyles.compactDocumentHeader}>
                  <div style={panelStyles.compactDocumentLabel}>{document.label}</div>
                  <ValidStatusPill status={document.status} />
                </div>
                {onboarding && onboarding.driver_visibility ? (
                  <div><strong>Shown To Customer:</strong> {onboarding.driver_visibility.enabled ? 'Yes' : 'No'}</div>
                ) : null}
                {onboarding && onboarding.profile_photo_guidance && onboarding.profile_photo_guidance.length > 0 ? (
                  <ul style={panelStyles.guidanceList}>
                    {onboarding.profile_photo_guidance.map(item => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </div>
          ))}

          <h5 style={panelStyles.sectionTitle}>Expiring Soon</h5>
          {onboardingExpiringCards.length > 0 ? (
            <div style={panelStyles.documentGrid}>
              {onboardingExpiringCards.map(document => (
                <DocumentCard
                  key={`onboarding-expiring-${document.type}-${document.vehicle_id || 'user'}-${document.file_name || 'current'}`}
                  document={document}
                  driverAuth0Id={resolvedDriverAuth0Id}
                  driverIdentifier={lookupIdentifier}
                  {...getDocumentUploadProps(document)}
                  {...getDocumentExpiryEditProps(document)}
                  {...getDocumentReviewProps(document)}
                />
              ))}
            </div>
          ) : (
            <p style={panelStyles.copy}>No onboarding documents are currently inside the reminder window.</p>
          )}

          <h5 style={panelStyles.sectionTitle}>Required Documents</h5>
          <div style={panelStyles.documentGrid}>
            {requiredDocumentCards.map(document => (
              <DocumentCard key={document.type} document={document} driverAuth0Id={resolvedDriverAuth0Id} driverIdentifier={lookupIdentifier} {...getDocumentExpiryEditProps(document)} {...getDocumentReviewProps(document)} />
            ))}
          </div>

          <h5 style={panelStyles.sectionTitle}>Credentials</h5>
          <div style={panelStyles.documentGrid}>
            {credentialDocumentCards.map(document => (
              <DocumentCard key={document.type} document={document} driverAuth0Id={resolvedDriverAuth0Id} driverIdentifier={lookupIdentifier} {...getDocumentExpiryEditProps(document)} {...getDocumentReviewProps(document)} />
            ))}
          </div>

          <h5 style={panelStyles.sectionTitle}>Trailer Assets</h5>
          {assets && assets.length > 0 ? (
            <div style={panelStyles.assetsGrid}>
              {assets.map(asset => (
                <div key={asset.asset_id} style={panelStyles.assetCard}>
                  <div><strong>{asset.asset_label || asset.asset_type}</strong></div>
                  <div>{asset.length_feet ? `${asset.length_feet} ft` : 'No length recorded'}</div>
                  <div>{asset.description || 'No description'}</div>
                </div>
              ))}
            </div>
          ) : (
            <p style={panelStyles.copy}>No trailer assets registered.</p>
          )}
        </>
      ) : null}

      {showEditor && !isDispatchVariant ? renderProfileEditor(false) : null}
    </div>
  );
};

export default DriverCompliancePanel;